import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaSave, FaFolderOpen, FaTrash, FaPlus, FaMinus, FaTrophy } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { API_BASE_URL } from '../utils/apiConfig';
import AuthService from '../services/AuthService';
import { useUser } from '../context/UserContext';
import { getEntitlements } from '../utils/entitlements';
import ProUpgradeCard from './ProUpgradeCard';
import './GameOfLife.css';

// ============================================
// GAME DATA — CAREERS WITH BENEFITS
// ============================================

const CAREERS = {
  noCollege: [
    { title: 'Electrician', salary: 48000, emoji: '⚡', growth: 0.02,
      benefits: { healthIns: true, match401k: 0.03, dental: false, pto: 10, signingBonus: 0, description: '🛡️ Health ins · 3% 401k match · 10 PTO days' } },
    { title: 'Real Estate Agent', salary: 42000, emoji: '🏠', growth: 0.035,
      benefits: { healthIns: false, match401k: 0, dental: false, pto: 0, signingBonus: 0, description: '❌ No benefits (independent contractor) · Unlimited schedule' } },
    { title: 'Mechanic', salary: 40000, emoji: '🔧', growth: 0.015,
      benefits: { healthIns: true, match401k: 0.02, dental: true, pto: 8, signingBonus: 500, description: '🛡️ Health + 🦷 Dental · 2% match · 8 PTO · $500 bonus' } },
    { title: 'Firefighter', salary: 50000, emoji: '🚒', growth: 0.02,
      benefits: { healthIns: true, match401k: 0.06, dental: true, pto: 15, signingBonus: 0, description: '🛡️ Health + 🦷 Dental · 6% 401k match · 15 PTO · Pension' } },
    { title: 'Sales Rep', salary: 38000, emoji: '🤝', growth: 0.04,
      benefits: { healthIns: true, match401k: 0.02, dental: false, pto: 10, signingBonus: 1000, description: '🛡️ Health · 2% match · 10 PTO · $1K bonus + commission' } },
    { title: 'Chef', salary: 36000, emoji: '👨‍🍳', growth: 0.02,
      benefits: { healthIns: false, match401k: 0, dental: false, pto: 5, signingBonus: 0, description: '❌ No insurance · 5 PTO · Free meals' } },
    { title: 'Plumber', salary: 47000, emoji: '🔧', growth: 0.025,
      benefits: { healthIns: true, match401k: 0.03, dental: false, pto: 10, signingBonus: 0, description: '🛡️ Health · 3% match · 10 PTO' } },
    { title: 'Construction Foreman', salary: 52000, emoji: '🏗️', growth: 0.02,
      benefits: { healthIns: true, match401k: 0.04, dental: true, pto: 12, signingBonus: 1500, description: '🛡️ Health + 🦷 Dental · 4% match · 12 PTO · $1.5K bonus' } },
    { title: 'Truck Driver', salary: 45000, emoji: '🚛', growth: 0.015,
      benefits: { healthIns: true, match401k: 0.02, dental: false, pto: 8, signingBonus: 3000, description: '🛡️ Health · 2% match · 8 PTO · $3K signing bonus' } },
    { title: 'Welder', salary: 44000, emoji: '🔥', growth: 0.02,
      benefits: { healthIns: true, match401k: 0.03, dental: true, pto: 10, signingBonus: 1000, description: '🛡️ Health + 🦷 Dental · 3% match · 10 PTO · $1K bonus' } },
  ],
  college: [
    { title: 'Software Engineer', salary: 82000, emoji: '💻', growth: 0.04,
      benefits: { healthIns: true, match401k: 0.06, dental: true, pto: 20, signingBonus: 10000, description: '🛡️ Health + 🦷 Dental · 6% match · 20 PTO · $10K bonus · RSUs' } },
    { title: 'Doctor', salary: 120000, emoji: '🩺', growth: 0.03,
      benefits: { healthIns: true, match401k: 0.05, dental: true, pto: 15, signingBonus: 25000, description: '🛡️ Health + 🦷 Dental · 5% match · 15 PTO · $25K bonus' } },
    { title: 'Lawyer', salary: 90000, emoji: '⚖️', growth: 0.035,
      benefits: { healthIns: true, match401k: 0.04, dental: true, pto: 15, signingBonus: 15000, description: '🛡️ Health + 🦷 Dental · 4% match · 15 PTO · $15K bonus' } },
    { title: 'Financial Analyst', salary: 72000, emoji: '📊', growth: 0.04,
      benefits: { healthIns: true, match401k: 0.06, dental: true, pto: 18, signingBonus: 5000, description: '🛡️ Health + 🦷 Dental · 6% match · 18 PTO · $5K bonus' } },
    { title: 'Teacher', salary: 48000, emoji: '📚', growth: 0.02,
      benefits: { healthIns: true, match401k: 0.05, dental: true, pto: 60, signingBonus: 0, description: '🛡️ Health + 🦷 Dental · 5% match · 60 PTO · Pension · Summers off' } },
    { title: 'Accountant', salary: 62000, emoji: '🧮', growth: 0.025,
      benefits: { healthIns: true, match401k: 0.04, dental: true, pto: 15, signingBonus: 3000, description: '🛡️ Health + 🦷 Dental · 4% match · 15 PTO · $3K bonus' } },
    { title: 'Marketing Manager', salary: 68000, emoji: '📈', growth: 0.03,
      benefits: { healthIns: true, match401k: 0.03, dental: false, pto: 18, signingBonus: 5000, description: '🛡️ Health · 3% match · 18 PTO · $5K bonus · Perks' } },
    { title: 'Nurse Practitioner', salary: 78000, emoji: '💉', growth: 0.025,
      benefits: { healthIns: true, match401k: 0.05, dental: true, pto: 20, signingBonus: 8000, description: '🛡️ Health + 🦷 Dental · 5% match · 20 PTO · $8K bonus' } },
    { title: 'Data Scientist', salary: 88000, emoji: '🔬', growth: 0.045,
      benefits: { healthIns: true, match401k: 0.06, dental: true, pto: 22, signingBonus: 12000, description: '🛡️ Health + 🦷 Dental · 6% match · 22 PTO · $12K bonus · RSUs' } },
    { title: 'Architect', salary: 70000, emoji: '🏛️', growth: 0.025,
      benefits: { healthIns: true, match401k: 0.04, dental: true, pto: 15, signingBonus: 3000, description: '🛡️ Health + 🦷 Dental · 4% match · 15 PTO · $3K bonus' } },
    { title: 'Pharmacist', salary: 95000, emoji: '💊', growth: 0.02,
      benefits: { healthIns: true, match401k: 0.05, dental: true, pto: 15, signingBonus: 10000, description: '🛡️ Health + 🦷 Dental · 5% match · 15 PTO · $10K bonus' } },
    { title: 'Civil Engineer', salary: 75000, emoji: '🌉', growth: 0.025,
      benefits: { healthIns: true, match401k: 0.05, dental: true, pto: 15, signingBonus: 5000, description: '🛡️ Health + 🦷 Dental · 5% match · 15 PTO · $5K bonus' } },
  ]
};

const INVESTMENT_TYPES = [
  { key: 'sp500', name: 'S&P 500 Index', risk: 'moderate', avgReturn: 0.10, volatility: 0.16, emoji: '📊', minAge: 0 },
  { key: 'bonds', name: 'Bonds', risk: 'low', avgReturn: 0.04, volatility: 0.05, emoji: '📜', minAge: 0 },
  { key: 'tech', name: 'Tech Stocks', risk: 'high', avgReturn: 0.14, volatility: 0.25, emoji: '💻', minAge: 14 },
  { key: 'realEstate', name: 'Real Estate Fund', risk: 'moderate', avgReturn: 0.08, volatility: 0.12, emoji: '🏘️', minAge: 18 },
  { key: 'crypto', name: 'Crypto', risk: 'very high', avgReturn: 0.15, volatility: 0.60, emoji: '₿', minAge: 18 },
];

const DICE_EVENTS = {
  1: { label: 'Bull Market', emoji: '📈', type: 'boom' },
  2: { label: 'Steady Year', emoji: '📊', type: 'normal' },
  3: { label: 'Unexpected Expense', emoji: '💸', type: 'expense' },
  4: { label: 'Opportunity Knocks', emoji: '🎯', type: 'opportunity' },
  5: { label: 'Market Downturn', emoji: '📉', type: 'downturn' },
  6: { label: 'Major Event', emoji: '🎲', type: 'major' },
};

const EXPENSE_EVENTS = [
  { text: 'Car repair needed', min: 500, max: 2500, emoji: '🚗' },
  { text: 'Medical bill', min: 500, max: 8000, emoji: '🏥', insuranceHelps: true },
  { text: 'Home appliance broke', min: 300, max: 2000, emoji: '🧊' },
  { text: 'Legal fees', min: 1000, max: 5000, emoji: '⚖️' },
  { text: 'Emergency travel', min: 500, max: 3000, emoji: '✈️' },
  { text: 'Pet emergency', min: 300, max: 2500, emoji: '🐕' },
  { text: 'Roof leak / water damage', min: 1500, max: 6000, emoji: '🌧️', needsHome: true },
  { text: 'Identity theft costs', min: 500, max: 3000, emoji: '😱' },
  { text: 'Tax audit — owed back taxes', min: 2000, max: 8000, emoji: '📋', minAge: 18 },
  { text: 'Fender bender', min: 400, max: 2000, emoji: '💥' },
  { text: 'Dental work needed', min: 500, max: 4000, emoji: '🦷', dentalHelps: true },
];

const EXPENSE_EVENTS_KIDS = [
  { text: 'Broke your phone', min: 50, max: 200, emoji: '📱' },
  { text: 'Bike repair needed', min: 20, max: 80, emoji: '🚲' },
  { text: 'Lost money', min: 5, max: 40, emoji: '🤦' },
  { text: 'School supplies needed', min: 30, max: 100, emoji: '📚' },
];

const MAJOR_EVENTS = [
  { text: 'Recession hits — company downsizes', type: 'recession', emoji: '🏭', minAge: 18 },
  { text: 'Inherited money from a relative!', type: 'inheritance', emoji: '💎', minAge: 0 },
  { text: 'Won a lawsuit settlement!', type: 'windfall', emoji: '🏆', minAge: 18 },
  { text: 'Housing market crash!', type: 'housing_crash', emoji: '🏚️', minAge: 18 },
  { text: 'Tech boom — stocks surge!', type: 'tech_boom', emoji: '🚀', minAge: 0 },
  { text: 'Inflation spikes!', type: 'inflation', emoji: '💹', minAge: 0 },
  { text: 'Got scammed — lost money', type: 'scam', emoji: '🦹', minAge: 14 },
  { text: 'Side business took off!', type: 'business', emoji: '🌟', minAge: 16 },
  { text: 'Natural disaster damage', type: 'disaster', emoji: '🌪️', needsHome: true, minAge: 18 },
  { text: 'Company went public — stock options!', type: 'ipo', emoji: '🔔', minAge: 22 },
  { text: 'Got a tax refund!', type: 'refund', emoji: '📬', minAge: 18 },
  { text: 'Won a bonus at work!', type: 'workBonus', emoji: '🎉', minAge: 18 },
];

const MAJOR_EVENTS_KIDS = [
  { text: 'Won a spelling bee prize!', type: 'prize', emoji: '🏆' },
  { text: 'Birthday money from relatives!', type: 'birthday', emoji: '🎂' },
  { text: 'Found money on the ground!', type: 'found', emoji: '💵' },
  { text: 'Parents added to your investment account!', type: 'parent_gift', emoji: '👨‍👩‍👦' },
  { text: 'Holiday gift money!', type: 'holiday', emoji: '🎄' },
];

// ============================================
// PURCHASABLE ITEMS — Fun Stuff (Buy tab)
// ============================================
const PURCHASABLE_ITEMS = {
  kid: [
    { name: 'Video Game', cost: 60, emoji: '🎮', happiness: 5 },
    { name: 'Bicycle', cost: 250, emoji: '🚲', happiness: 8 },
    { name: 'Phone', cost: 400, emoji: '📱', happiness: 10 },
    { name: 'Skateboard', cost: 80, emoji: '🛹', happiness: 4 },
    { name: 'Laptop', cost: 800, emoji: '💻', happiness: 15 },
    { name: 'Clothes/Shoes', cost: 150, emoji: '👟', happiness: 5 },
  ],
  teen: [
    { name: 'Video Game', cost: 70, emoji: '🎮', happiness: 3 },
    { name: 'Phone Upgrade', cost: 600, emoji: '📱', happiness: 8 },
    { name: 'Laptop', cost: 1000, emoji: '💻', happiness: 12 },
    { name: 'Concert Tickets', cost: 150, emoji: '🎵', happiness: 10 },
    { name: 'Clothes/Shoes', cost: 200, emoji: '👟', happiness: 5 },
    { name: 'Gaming Console', cost: 500, emoji: '🕹️', happiness: 12 },
    { name: 'Prom Outfit', cost: 300, emoji: '👔', happiness: 8, minAge: 16 },
  ],
  adult: [
    { name: 'Vacation', cost: 3000, emoji: '✈️', happiness: 25 },
    { name: 'Luxury Vacation', cost: 8000, emoji: '🏝️', happiness: 35 },
    { name: 'New Wardrobe', cost: 2000, emoji: '👔', happiness: 10 },
    { name: 'New Phone', cost: 1200, emoji: '📱', happiness: 5 },
    { name: 'Big Screen TV', cost: 2000, emoji: '📺', happiness: 8 },
    { name: 'Engagement Ring', cost: 5000, emoji: '💍', happiness: 30, once: 'ring' },
    { name: 'Wedding', cost: 20000, emoji: '💒', happiness: 40, once: 'wedding', requires: 'ring' },
    { name: 'Pet', cost: 1500, emoji: '🐕', happiness: 20, once: 'pet' },
    { name: 'Gym Membership', cost: 600, emoji: '💪', happiness: 8 },
    { name: 'Hobby Equipment', cost: 1000, emoji: '🎨', happiness: 10 },
  ],
  retired: [
    { name: 'Vacation', cost: 3000, emoji: '✈️', happiness: 25 },
    { name: 'Luxury Cruise', cost: 10000, emoji: '🚢', happiness: 40 },
    { name: 'Gift to Grandkids', cost: 2000, emoji: '🎁', happiness: 30 },
    { name: 'Hobby Equipment', cost: 1000, emoji: '🎨', happiness: 12 },
    { name: 'Golf Membership', cost: 3000, emoji: '⛳', happiness: 15 },
    { name: 'New Phone', cost: 1200, emoji: '📱', happiness: 5 },
  ],
};

// ============================================
// LONG-TERM ASSETS (Assets tab)
// ============================================
const ASSET_ITEMS = {
  teen: [
    { name: 'Used Car', cost: 5000, emoji: '🚗', happiness: 25, minAge: 16, assetValue: 3000, depreciation: 0.15, category: 'car' },
  ],
  adult: [
    { name: 'Used Car', cost: 12000, emoji: '🚙', happiness: 12, assetValue: 10000, depreciation: 0.12, category: 'car' },
    { name: 'New Car', cost: 28000, emoji: '🚗', happiness: 20, assetValue: 28000, depreciation: 0.15, category: 'car' },
    { name: 'Luxury Car', cost: 55000, emoji: '🏎️', happiness: 30, assetValue: 55000, depreciation: 0.18, category: 'car' },
    { name: 'Home Renovation', cost: 15000, emoji: '🔨', happiness: 15, needsHome: true, assetValue: 12000, depreciation: 0, category: 'home', addsToHome: true },
    { name: 'Solar Panels', cost: 20000, emoji: '☀️', happiness: 8, needsHome: true, assetValue: 15000, depreciation: 0.02, once: 'solar', category: 'home', addsToHome: true },
    { name: 'Boat', cost: 25000, emoji: '⛵', happiness: 18, assetValue: 20000, depreciation: 0.10, category: 'vehicle' },
    { name: 'Rental Property', cost: 120000, emoji: '🏘️', happiness: 10, assetValue: 120000, depreciation: -0.03, minAge: 25, once: 'rental', category: 'property', income: 9600 },
    { name: 'Home Pool', cost: 30000, emoji: '🏊', happiness: 20, needsHome: true, assetValue: 15000, depreciation: 0.05, once: 'pool', category: 'home' },
  ],
  retired: [
    { name: 'New Car', cost: 30000, emoji: '🚗', happiness: 15, assetValue: 30000, depreciation: 0.15, category: 'car' },
    { name: 'Home Renovation', cost: 15000, emoji: '🔨', happiness: 15, needsHome: true, assetValue: 12000, depreciation: 0, category: 'home', addsToHome: true },
    { name: 'RV / Camper', cost: 45000, emoji: '🚐', happiness: 25, assetValue: 40000, depreciation: 0.10, once: 'rv', category: 'vehicle' },
    { name: 'Downsize Home', cost: 0, emoji: '🏡', happiness: 5, needsHome: true, once: 'downsize', category: 'home', special: 'downsize' },
  ],
};

// ============================================
// FINANCIAL PROTECTORS (Protect tab)
// ============================================
const PROTECTOR_ITEMS = [
  {
    id: 'health',
    name: 'Health Insurance',
    emoji: '🏥',
    annualCost: 3600,
    description: 'Covers 85% of medical expenses. Essential protection against big bills.',
    benefitText: '85% medical coverage',
    minAge: 18,
    effect: 'health',
  },
  {
    id: 'dental',
    name: 'Dental Insurance',
    emoji: '🦷',
    annualCost: 600,
    description: 'Covers 80% of dental bills. Prevents surprise dental costs.',
    benefitText: '80% dental coverage',
    minAge: 18,
    effect: 'dental',
  },
  {
    id: 'life',
    name: 'Life Insurance',
    emoji: '💀',
    annualCost: 1200,
    description: 'Provides payout to family on death. Protects loved ones financially.',
    benefitText: '$250K death benefit to family',
    minAge: 22,
    effect: 'life',
  },
  {
    id: 'disability',
    name: 'Disability Insurance',
    emoji: '♿',
    annualCost: 1800,
    description: 'Replaces 60% of income if you become unable to work.',
    benefitText: '60% income replacement',
    minAge: 18,
    effect: 'disability',
  },
  {
    id: 'umbrella',
    name: 'Umbrella Policy',
    emoji: '☂️',
    annualCost: 400,
    description: 'Extra liability coverage beyond home/auto. Protects assets from lawsuits.',
    benefitText: '$1M+ liability protection',
    minAge: 25,
    effect: 'umbrella',
  },
  {
    id: 'emergency',
    name: 'Emergency Fund',
    emoji: '🏦',
    annualCost: 0,
    buyCost: 10000,
    description: 'Set aside 3-6 months of expenses. Prevents debt from surprise costs.',
    benefitText: 'Absorbs 50% of unexpected expenses',
    minAge: 18,
    effect: 'emergency',
    isOneTime: true,
  },
];

const HOUSE_OPTIONS = [
  { name: 'Starter Home', cost: 180000, emoji: '🏡', appreciation: 0.03, maintenance: 3000 },
  { name: 'Condo', cost: 250000, emoji: '🏢', appreciation: 0.035, maintenance: 4000 },
  { name: 'Family House', cost: 400000, emoji: '🏠', appreciation: 0.04, maintenance: 6000 },
  { name: 'Keep Renting', cost: 0, emoji: '🏚️', appreciation: 0, maintenance: 0, rent: 18000 },
];

const FINANCIAL_TIPS = [
  "💡 Start investing early — compound interest is powerful!",
  "💡 Keep 3-6 months of expenses in an emergency fund.",
  "💡 Max out your 401(k) employer match — it's free money!",
  "💡 Diversify your investments to reduce risk.",
  "💡 Pay off high-interest debt before investing.",
  "💡 Inflation averages ~3%/year — cash loses value over time.",
  "💡 The S&P 500 has returned ~10%/year since 1926.",
  "💡 Social Security replaces only ~40% of pre-retirement income.",
  "💡 Healthcare is the #1 expense in retirement.",
  "💡 The 4% rule: withdraw 4% of savings per year in retirement.",
  "💡 College grads earn ~$1M more over a lifetime on average.",
  "💡 Homeownership builds equity — renting builds nothing.",
  "💡 $1,000 invested at birth at 10%/yr = $117,000 at age 50!",
  "💡 Even $5/week invested as a teen adds up over decades.",
  "💡 Warren Buffett bought his first stock at age 11!",
  "💡 Compound interest is the 8th wonder of the world — Einstein",
  "💡 Extra loan payments save thousands in interest over time!",
  "💡 Job benefits can be worth $10-30K/year on top of salary!",
  "💡 A 6% employer 401k match on $80K salary = $4,800/yr FREE.",
  "💡 Paying off debt early reduces total interest paid significantly.",
];

// ============================================
// ITEMIZED EXPENSE GENERATOR
// ============================================
const generateItemizedExpenses = (currentAge, inflation, isRetiredNow, numKids, hasHome, isInCollege) => {
  const items = [];
  const m = inflation; // inflation multiplier

  if (currentAge < 12) {
    // Child — parents pay most, but some costs tracked
    const schoolSupplies = Math.round(randBetween(80, 200) * m);
    const snacks = Math.round(randBetween(100, 300) * m);
    const clothing = Math.round(randBetween(200, 500) * m);
    const activities = Math.round(randBetween(100, 400) * m);
    items.push({ label: '📒 School supplies', amount: schoolSupplies });
    items.push({ label: '🍪 Snacks & treats', amount: snacks });
    items.push({ label: '👕 Clothing', amount: clothing });
    items.push({ label: '⚽ Activities & sports', amount: activities });
    return items;
  }

  if (currentAge >= 12 && currentAge < 18) {
    // Teen
    const food = Math.round(randBetween(1200, 2400) * m);
    const mealsOut = Math.round(randBetween(3, 8)); // times per month
    const mealsOutCost = Math.round(mealsOut * 12 * randBetween(10, 18) * m);
    const clothing = Math.round(randBetween(400, 900) * m);
    const phone = Math.round(randBetween(30, 50) * 12 * m);
    const entertainment = Math.round(randBetween(300, 700) * m);
    const schoolSupplies = Math.round(randBetween(100, 300) * m);
    const transport = currentAge >= 16 ? Math.round(randBetween(800, 1500) * m) : 0;
    const hygiene = Math.round(randBetween(150, 300) * m);

    items.push({ label: `🍞 Groceries & food at home`, amount: food, detail: `~${fmt(Math.round(food / 52))}/week` });
    items.push({ label: `🍔 Eating out (${mealsOut}×/month)`, amount: mealsOutCost, detail: `~${fmt(Math.round(mealsOutCost / 12))}/month` });
    items.push({ label: '👕 Clothing & shoes', amount: clothing });
    items.push({ label: '📱 Phone bill', amount: phone, detail: `${fmt(Math.round(phone / 12))}/month` });
    items.push({ label: '🎬 Entertainment & subscriptions', amount: entertainment });
    items.push({ label: '📒 School supplies', amount: schoolSupplies });
    if (transport > 0) items.push({ label: '⛽ Gas & car maintenance', amount: transport });
    items.push({ label: '🧴 Personal care & hygiene', amount: hygiene });
    return items;
  }

  if (isInCollege) {
    // College student — housing handled separately
    const mealPlan = Math.round(randBetween(3000, 5000) * m);
    const mealsOut = Math.round(randBetween(2, 5));
    const mealsOutCost = Math.round(mealsOut * 12 * randBetween(10, 16) * m);
    const textbooks = Math.round(randBetween(400, 800) * m);
    const phone = Math.round(randBetween(40, 60) * 12 * m);
    const clothing = Math.round(randBetween(300, 600) * m);
    const entertainment = Math.round(randBetween(400, 800) * m);
    const transport = Math.round(randBetween(200, 600) * m);
    const hygiene = Math.round(randBetween(200, 400) * m);

    items.push({ label: '🍽️ Meal plan / groceries', amount: mealPlan, detail: `~${fmt(Math.round(mealPlan / 12))}/month` });
    items.push({ label: `🍔 Eating out (${mealsOut}×/month)`, amount: mealsOutCost });
    items.push({ label: '📚 Textbooks & supplies', amount: textbooks });
    items.push({ label: '📱 Phone bill', amount: phone, detail: `${fmt(Math.round(phone / 12))}/month` });
    items.push({ label: '👕 Clothing', amount: clothing });
    items.push({ label: '🎬 Entertainment & social', amount: entertainment });
    items.push({ label: '🚌 Transportation', amount: transport });
    items.push({ label: '🧴 Personal care', amount: hygiene });
    return items;
  }

  // Adults (18+ working) and retirees
  const isOlder = currentAge >= 50;

  // === FOOD ===
  const groceryWeekly = isRetiredNow
    ? randBetween(70, 120)
    : isOlder ? randBetween(80, 140) : currentAge < 25 ? randBetween(50, 90) : randBetween(65, 130);
  const groceryMultiplier = 1 + (numKids * 0.3); // each kid adds 30%
  const groceries = Math.round(groceryWeekly * 52 * groceryMultiplier * m);
  const breadLoaves = Math.round(randBetween(40, 80)); // loaves per year
  const eggsDoz = Math.round(randBetween(30, 60));
  const milkGal = Math.round(randBetween(40, 70));
  items.push({
    label: '🛒 Groceries',
    amount: groceries,
    detail: `~${fmt(Math.round(groceries / 52))}/week · ${breadLoaves} loaves bread · ${eggsDoz} doz eggs · ${milkGal} gal milk`
  });

  // === EATING OUT ===
  const mealsOutPerMonth = isRetiredNow
    ? Math.round(randBetween(3, 8))
    : currentAge < 25 ? Math.round(randBetween(4, 10)) : Math.round(randBetween(3, 8));
  const avgMealCost = isRetiredNow
    ? randBetween(14, 22) : currentAge < 25 ? randBetween(10, 18) : randBetween(12, 25);
  const eatingOut = Math.round(mealsOutPerMonth * 12 * avgMealCost * m);
  const coffeesPerWeek = Math.round(randBetween(1, 5));
  const coffeeCost = Math.round(coffeesPerWeek * 52 * randBetween(4, 7) * m);
  items.push({
    label: `🍔 Dining out (${mealsOutPerMonth}×/month)`,
    amount: eatingOut,
    detail: `~${fmt(Math.round(avgMealCost * m))}/meal`
  });
  items.push({
    label: `☕ Coffee (${coffeesPerWeek}×/week)`,
    amount: coffeeCost,
    detail: `~${fmt(Math.round(coffeeCost / 52))}/week`
  });

  // === UTILITIES ===
  const electric = Math.round(randBetween(80, 180) * 12 * m);
  const water = Math.round(randBetween(30, 70) * 12 * m);
  const internet = Math.round(randBetween(50, 90) * 12 * m);
  const gas = hasHome ? Math.round(randBetween(40, 100) * 12 * m) : 0;
  items.push({ label: '⚡ Electric', amount: electric, detail: `${fmt(Math.round(electric / 12))}/month` });
  items.push({ label: '💧 Water', amount: water, detail: `${fmt(Math.round(water / 12))}/month` });
  items.push({ label: '🌐 Internet', amount: internet, detail: `${fmt(Math.round(internet / 12))}/month` });
  if (gas > 0) items.push({ label: '🔥 Natural gas / heating', amount: gas, detail: `${fmt(Math.round(gas / 12))}/month` });

  // === PHONE ===
  const phoneBill = Math.round(randBetween(50, 90) * 12 * m);
  items.push({ label: '📱 Phone bill', amount: phoneBill, detail: `${fmt(Math.round(phoneBill / 12))}/month` });

  // === TRANSPORTATION ===
  // Gas: Realistic $100-150/month for single person
  const gasCost = Math.round(randBetween(100, 150) * 12 * m);
  // Car insurance: Age-dependent (younger = higher), single person
  const insuranceMonthly = currentAge < 25 
    ? randBetween(120, 180)  // Young drivers pay more
    : currentAge < 30
    ? randBetween(100, 150)  // Still higher risk
    : randBetween(80, 120);   // Mature drivers
  const carInsurance = Math.round(insuranceMonthly * 12 * m);
  items.push({ label: '⛽ Gas for car', amount: gasCost, detail: `${fmt(Math.round(gasCost / 12))}/month` });
  items.push({ label: '🚗 Auto insurance', amount: carInsurance, detail: `${fmt(Math.round(carInsurance / 12))}/month` });

  // === CLOTHING ===
  const clothing = Math.round((isRetiredNow ? randBetween(400, 800) : randBetween(600, 1500)) * m);
  items.push({ label: '👕 Clothing & shoes', amount: clothing });

  // === ENTERTAINMENT ===
  const streaming = Math.round(randBetween(15, 40) * 12 * m);
  const entertainment = Math.round(randBetween(500, 1500) * m);
  items.push({ label: '📺 Streaming subscriptions', amount: streaming, detail: `${fmt(Math.round(streaming / 12))}/month` });
  items.push({ label: '🎬 Entertainment & outings', amount: entertainment });

  // === PERSONAL CARE ===
  const hygiene = Math.round(randBetween(300, 700) * m);
  const haircuts = Math.round(randBetween(4, 12)); // per year
  items.push({ label: `🧴 Personal care (${haircuts} haircuts/yr)`, amount: hygiene });

  // === HEALTHCARE (out-of-pocket co-pays etc) ===
  const healthOOP = Math.round((isOlder ? randBetween(800, 2000) : randBetween(200, 600)) * m);
  const doctorVisits = Math.round(isOlder ? randBetween(4, 10) : randBetween(1, 4));
  items.push({
    label: `🏥 Healthcare (${doctorVisits} doctor visits)`,
    amount: healthOOP,
    detail: `co-pays, prescriptions, etc.`
  });

  // === KIDS COSTS ===
  if (numKids > 0) {
    const kidsClothing = Math.round(numKids * randBetween(400, 800) * m);
    const kidsActivities = Math.round(numKids * randBetween(500, 1500) * m);
    const kidsSchool = Math.round(numKids * randBetween(200, 600) * m);
    const kidsDaycare = currentAge < 40 && numKids > 0 ? Math.round(numKids * randBetween(3000, 8000) * m) : 0;
    items.push({ label: `👶 Kids clothing (×${numKids})`, amount: kidsClothing });
    items.push({ label: `⚽ Kids activities & sports (×${numKids})`, amount: kidsActivities });
    items.push({ label: `📒 Kids school supplies (×${numKids})`, amount: kidsSchool });
    if (kidsDaycare > 0) items.push({ label: `🏫 Childcare/daycare (×${numKids})`, amount: kidsDaycare });
  }

  // === MISC ===
  const gifts = Math.round(randBetween(200, 800) * m);
  const homeSupplies = Math.round(randBetween(200, 500) * m);
  items.push({ label: '🎁 Gifts & holidays', amount: gifts });
  items.push({ label: '🧹 Home supplies & cleaning', amount: homeSupplies });

  // === RETIREMENT EXTRAS ===
  if (isRetiredNow) {
    const hobbies = Math.round(randBetween(500, 1500) * m);
    items.push({ label: '🎨 Hobbies & leisure', amount: hobbies });
  }

  return items;
};

// ============================================
// HELPERS
// ============================================
const fmt = (n) => {
  const val = Math.round(n);
  if (val < 0) return `-$${Math.abs(val).toLocaleString()}`;
  return `$${val.toLocaleString()}`;
};
const rand = (arr) => arr[Math.floor(Math.random() * arr.length)];
const randBetween = (min, max) => min + Math.random() * (max - min);
const apiHeaders = () => {
  const token = AuthService.getToken();
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
};

const emptyPortfolio = () => ({ sp500: 0, bonds: 0, tech: 0, realEstate: 0, crypto: 0 });
const totalInvestments = (p) => Object.values(p).reduce((s, v) => s + v, 0);

// ============================================
// COMPONENT
// ============================================
const GameOfLife = () => {
  // Core
  const [gameState, setGameState] = useState('menu');
  const [age, setAge] = useState(0);

  // Finances
  const [cash, setCash] = useState(0);
  const [salary, setSalary] = useState(0);
  const [career, setCareer] = useState(null);
  const [hasCollege, setHasCollege] = useState(false);
  const [inCollege, setInCollege] = useState(false);
  const [collegeYear, setCollegeYear] = useState(0);
  const [studentLoan, setStudentLoan] = useState(0);
  const [studentLoanRate] = useState(0.05); // 5% interest
  const [mortgage, setMortgage] = useState(0);
  const [mortgageRate] = useState(0.04); // 4% interest
  const [house, setHouse] = useState(null);
  const [houseValue, setHouseValue] = useState(0);
  const [currentRent, setCurrentRent] = useState(null); // Current rent amount (null = not renting yet)
  const [portfolio, setPortfolio] = useState(emptyPortfolio());
  const [retirement401k, setRetirement401k] = useState(0);
  const [ssContribYears, setSsContribYears] = useState(0);

  // Income tracking
  const [choreIncome, setChoreIncome] = useState(0);
  const [partTimeIncome, setPartTimeIncome] = useState(0);

  // Life / benefits
  const [hasInsurance, setHasInsurance] = useState(false);
  const [hasDental, setHasDental] = useState(false);
  const [jobInsurance, setJobInsurance] = useState(false); // from employer
  const [jobDental, setJobDental] = useState(false);
  const [hasLifeInsurance, setHasLifeInsurance] = useState(false);
  const [hasDisability, setHasDisability] = useState(false);
  const [hasUmbrella, setHasUmbrella] = useState(false);
  const [hasEmergencyFund, setHasEmergencyFund] = useState(false);
  const [jobMatch, setJobMatch] = useState(0.03);
  const [isMarried, setIsMarried] = useState(false);
  const [kids, setKids] = useState(0);
  const [isRetired, setIsRetired] = useState(false);
  const [retiredAge, setRetiredAge] = useState(null);
  const [happiness, setHappiness] = useState(50);
  const [purchasedOnce, setPurchasedOnce] = useState([]);
  // Owned long-term assets (cars, boats, rental properties, etc.)
  const [ownedAssets, setOwnedAssets] = useState([]); // { name, emoji, currentValue, depreciation, yearBought, category, income? }
  const [rentalIncome, setRentalIncome] = useState(0);

  // UI
  const [diceResult, setDiceResult] = useState(null);
  const [isRolling, setIsRolling] = useState(false);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [currentDecision, setCurrentDecision] = useState(null);
  const [eventLog, setEventLog] = useState([]);
  const [tip, setTip] = useState('');
  const [showLog, setShowLog] = useState(false);
  const [showSaveSlots, setShowSaveSlots] = useState(false);
  const [saveSlots, setSaveSlots] = useState([null, null, null]);
  const [savesLoading, setSavesLoading] = useState(false);
  const [yearSummary, setYearSummary] = useState(null);
  const [yearEvents, setYearEvents] = useState([]);
  const [investAmounts, setInvestAmounts] = useState({});
  const [withdrawAmounts, setWithdrawAmounts] = useState({});
  const [debtPayAmount, setDebtPayAmount] = useState({ student: '', mortgage: '' });
  const [actionsTab, setActionsTab] = useState('invest');
  const [recapData, setRecapData] = useState(null); // For age 1-18 recap

  // Leaderboard
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [leaderboardData, setLeaderboardData] = useState(null);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const [selectedLeaderboardEntry, setSelectedLeaderboardEntry] = useState(null);
  const [scoreSubmitted, setScoreSubmitted] = useState(false);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [playerRank, setPlayerRank] = useState(null);
  const [hasAlreadySubmitted, setHasAlreadySubmitted] = useState(false); // One score per user ever

  // Inflation
  const [inflationMultiplier, setInflationMultiplier] = useState(1.0);

  // Pending year
  const [pendingYear, setPendingYear] = useState(null);

  useEffect(() => { fetchSaves(); checkIfAlreadySubmitted(); }, []);

  const checkIfAlreadySubmitted = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/game-of-life/leaderboard?limit=1`, { headers: apiHeaders() });
      if (res.ok) {
        const data = await res.json();
        if (data.hasSubmitted) {
          setHasAlreadySubmitted(true);
        }
      }
    } catch (err) { /* ignore — will be checked again on submit */ }
  };

  const fetchSaves = async () => {
    try {
      setSavesLoading(true);
      const res = await fetch(`${API_BASE_URL}/game-of-life/saves`, { headers: apiHeaders() });
      if (res.ok) {
        const data = await res.json();
        setSaveSlots(data.slots || [null, null, null]);
      }
    } catch (e) {
      try {
        const saved = localStorage.getItem('investnyou-gol-saves-v4');
        if (saved) setSaveSlots(JSON.parse(saved));
      } catch (err) { /* ignore */ }
    } finally { setSavesLoading(false); }
  };

  const totalAssetValue = ownedAssets.reduce((sum, a) => sum + (a.currentValue || 0), 0);
  const getNetWorth = useCallback(() => {
    return Math.round(cash + totalInvestments(portfolio) + retirement401k + houseValue + totalAssetValue - studentLoan - mortgage);
  }, [cash, portfolio, retirement401k, houseValue, studentLoan, mortgage, totalAssetValue]);

  const addLog = useCallback((msg, type = 'info') => {
    setEventLog(prev => [{ message: msg, type, age, timestamp: Date.now() }, ...prev].slice(0, 100));
  }, [age]);

  // ---- Leaderboard API ----
  const fetchLeaderboard = async () => {
    try {
      setLeaderboardLoading(true);
      const res = await fetch(`${API_BASE_URL}/game-of-life/leaderboard`, { headers: apiHeaders() });
      if (res.ok) {
        const data = await res.json();
        setLeaderboardData(data);
        if (data.hasSubmitted) setHasAlreadySubmitted(true);
      } else {
        toast.error('Failed to load leaderboard');
      }
    } catch (err) {
      console.error('[GameOfLife] Leaderboard fetch error:', err);
      toast.error('Could not connect to leaderboard');
    } finally {
      setLeaderboardLoading(false);
    }
  };

  const submitScore = async (deathAge, nw, ratingStr, ratingEmo) => {
    try {
      const payload = {
        netWorth: Math.round(nw),
        deathAge,
        career: career?.title || null,
        careerEmoji: career?.emoji || null,
        rating: ratingStr,
        ratingEmoji: ratingEmo,
        hadCollege: hasCollege,
        wasMarried: isMarried,
        kids,
        homeType: house?.name || null,
        retiredAge: retiredAge || null,
        happiness,
        assets: ownedAssets || [],
      };
      const res = await fetch(`${API_BASE_URL}/game-of-life/leaderboard`, {
        method: 'POST',
        headers: apiHeaders(),
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const data = await res.json();
        setPlayerRank(data.rank);
        setScoreSubmitted(true);
        setHasAlreadySubmitted(true);
      } else if (res.status === 409) {
        // Already submitted (server-side guard)
        setHasAlreadySubmitted(true);
        setScoreSubmitted(false);
      }
    } catch (err) {
      console.error('[GameOfLife] Score submit error:', err);
    }
  };

  const openLeaderboard = () => {
    setSelectedLeaderboardEntry(null);
    setShowLeaderboard(true);
    fetchLeaderboard();
  };

  // Effective insurance check (from job or self-purchased)
  const effectiveInsurance = hasInsurance || jobInsurance;
  const effectiveDental = hasDental || jobDental;

  // ============================================
  // YEARLY PROCESSING
  // ============================================
  const rollDice = () => {
    if (isRolling || gameState !== 'playing') return;
    setIsRolling(true);
    setTip(rand(FINANCIAL_TIPS));

    let rolls = 0;
    const result = Math.floor(Math.random() * 6) + 1;
    const interval = setInterval(() => {
      setDiceResult(Math.floor(Math.random() * 6) + 1);
      rolls++;
      if (rolls >= 12) {
        clearInterval(interval);
        setDiceResult(result);
        setTimeout(() => {
          processYear(result);
          setIsRolling(false);
        }, 400);
      }
    }, 80);
  };

  const processYear = (dice) => {
    setShowBreakdown(false); // Reset expense breakdown toggle
    const newAge = age + 1;
    setAge(newAge);
    const diceEvent = DICE_EVENTS[dice];
    let events = [];
    let incomeThisYear = 0;
    let expensesThisYear = 0;

    // Inflation (~2-3.5%/yr — slightly more fair)
    const yearInflation = 1 + randBetween(0.018, 0.035);
    setInflationMultiplier(prev => prev * yearInflation);

    // ==== DEBT INTEREST (accrues yearly — added to balance) ====
    if (studentLoan > 0 && !inCollege) {
      const interest = Math.round(studentLoan * studentLoanRate);
      setStudentLoan(prev => prev + interest);
      events.push({ text: `🎓 Student loan interest accrued: ${fmt(interest)} (${(studentLoanRate * 100).toFixed(0)}% rate) — Balance now ~${fmt(studentLoan + interest)}`, emoji: '🎓' });
    }

    // ==== AGES 1-11: Baby/child ====
    if (newAge <= 11) {
      events.push({ text: newAge <= 5 ? 'Growing up! 👶' : 'Going to school! 🏫', emoji: newAge <= 5 ? '👶' : '🏫' });
      if (dice >= 3) {
        const gift = Math.round(randBetween(25, 120));
        setCash(prev => prev + gift);
        incomeThisYear += gift;
        events.push({ text: `🎂 Birthday/holiday money: +${fmt(gift)}`, emoji: '🎂' });
      }
    }

    // ==== AGE 12-13: Chores ====
    if (newAge >= 12 && newAge <= 13) {
      const weeklyChore = Math.round(randBetween(10, 25));
      const yearlyChore = weeklyChore * 52;
      setChoreIncome(yearlyChore);
      setCash(prev => prev + yearlyChore);
      incomeThisYear += yearlyChore;
      events.push({ text: `🧹 Chore money: ${fmt(weeklyChore)}/week = ${fmt(yearlyChore)}/year`, emoji: '🧹' });
    }

    // ==== AGE 14-17: Part-time ====
    if (newAge >= 14 && newAge <= 17 && !inCollege) {
      const hourlyWage = newAge >= 16 ? randBetween(10, 14) : randBetween(8, 12);
      const hoursPerWeek = newAge >= 16 ? randBetween(6, 12) : 3;
      const yearlyPartTime = Math.round(hourlyWage * hoursPerWeek * 50);
      setPartTimeIncome(yearlyPartTime);
      setCash(prev => prev + yearlyPartTime);
      incomeThisYear += yearlyPartTime;
      events.push({ text: `💼 Part-time: ${fmt(hourlyWage.toFixed(0))}/hr × ${hoursPerWeek.toFixed(0)} hrs/wk = ${fmt(yearlyPartTime)}/yr`, emoji: '💼' });
      const choreAmt = Math.round(randBetween(500, 1000));
      setCash(prev => prev + choreAmt);
      incomeThisYear += choreAmt;
    }

    // ==== AGE 18: College decision ====
    if (newAge === 18 && !career && !inCollege) {
      setYearEvents(events);
      showCareerDecision(newAge, events, diceEvent, incomeThisYear, expensesThisYear);
      return;
    }

    // ==== IN COLLEGE ====
    if (inCollege) {
      const yr = collegeYear + 1;
      setCollegeYear(yr);
      const tuition = Math.round(20000 * inflationMultiplier);
      setStudentLoan(prev => prev + tuition);
      const livingCost = Math.round(6000 * inflationMultiplier);
      expensesThisYear += livingCost;
      setCash(prev => prev - livingCost);
      events.push({ text: `🎓 College Year ${yr}/4 — Tuition: ${fmt(tuition)} (loans)`, emoji: '🎓' });

      const campusJob = Math.round(randBetween(4000, 8000));
      setCash(prev => prev + campusJob);
      incomeThisYear += campusJob;
      events.push({ text: `📚 Campus job: +${fmt(campusJob)}`, emoji: '📚' });

      if (yr >= 4) {
        setInCollege(false);
        setHasCollege(true);
        events.push({ text: `🎓 Graduated! Student loans: ${fmt(studentLoan + tuition)}`, emoji: '🎓' });
        setYearEvents(events);
        showPostCollegeCareerPick(newAge, events, diceEvent, incomeThisYear, expensesThisYear);
        return;
      }
    }

    // ==== CAREER INCOME (with benefits) ====
    if (career && !isRetired && !inCollege) {
      const raise = career.growth + randBetween(-0.005, 0.02);
      const newSalary = Math.round(salary * (1 + Math.max(0, raise)));
      setSalary(newSalary);

      // Tax rate (progressive-ish)
      const taxRate = newSalary > 150000 ? 0.30 : newSalary > 80000 ? 0.25 : newSalary > 40000 ? 0.22 : 0.15;
      const afterTax = Math.round(newSalary * (1 - taxRate));
      incomeThisYear += afterTax;
      setCash(prev => prev + afterTax);
      setSsContribYears(prev => prev + 1);

      // 401k (employee 6% + employer match from benefits)
      const contrib = Math.round(newSalary * 0.06);
      const match = Math.round(newSalary * jobMatch);
      setRetirement401k(prev => prev + contrib + match);
      setCash(prev => prev - contrib);
      expensesThisYear += contrib;

      events.push({ text: `${career.emoji} Earned ${fmt(afterTax)} (${(taxRate * 100).toFixed(0)}% tax) | 401k: +${fmt(contrib + match)} (${(jobMatch * 100).toFixed(0)}% match)`, emoji: career.emoji });

      // Spouse income if married
      if (isMarried) {
        const spouseIncome = Math.round(randBetween(25000, 40000));
        setCash(prev => prev + spouseIncome);
        incomeThisYear += spouseIncome;
        events.push({ text: `💍 Spouse income: +${fmt(spouseIncome)}`, emoji: '💍' });
      }
    }

    // ==== RETIREMENT INCOME ====
    if (isRetired) {
      const ssMonthly = Math.round((ssContribYears * (salary || 50000) * 0.015) / 12);
      const ssAnnual = ssMonthly * 12;
      setCash(prev => prev + ssAnnual);
      incomeThisYear += ssAnnual;
      events.push({ text: `🏛️ Social Security: ${fmt(ssAnnual)}/year`, emoji: '🏛️' });

      if (retirement401k > 0) {
        const withdrawal = Math.round(retirement401k * 0.04);
        setRetirement401k(prev => prev - withdrawal);
        setCash(prev => prev + withdrawal);
        incomeThisYear += withdrawal;
        events.push({ text: `🏦 401(k) withdrawal (4% rule): ${fmt(withdrawal)}`, emoji: '🏦' });
      }

      if (isMarried) {
        const spouseSS = Math.round(ssAnnual * 0.5);
        setCash(prev => prev + spouseSS);
        incomeThisYear += spouseSS;
        events.push({ text: `💍 Spouse SS benefits: +${fmt(spouseSS)}`, emoji: '💍' });
      }
    }

    // ==== LIVING EXPENSES (itemized) ====
    let yearlyExpenses = 0;
    let expenseBreakdown = []; // detailed line items

    if (newAge >= 18) {
      // Housing
      if (house && house.cost > 0) {
        const mortPay = mortgage > 0 ? Math.min(Math.round(mortgage * 0.06), mortgage) : 0;
        const mortInterest = mortgage > 0 ? Math.round(mortgage * mortgageRate) : 0;
        const mortPrincipal = Math.max(0, mortPay - mortInterest);
        if (mortPay > 0) setMortgage(prev => Math.max(0, prev - mortPrincipal));
        const maint = Math.round(house.maintenance * inflationMultiplier);
        const propTax = Math.round(houseValue * 0.011);
        yearlyExpenses += mortPay + maint + propTax;
        if (mortPay > 0) {
          events.push({ text: `🏠 Mortgage: ${fmt(mortPay)} (${fmt(mortInterest)} interest, ${fmt(mortPrincipal)} principal)`, emoji: '🏠' });
          expenseBreakdown.push({ label: '🏠 Mortgage payment', amount: mortPay, detail: `${fmt(mortInterest)} int + ${fmt(mortPrincipal)} principal` });
        }
        expenseBreakdown.push({ label: '🔧 Home maintenance', amount: maint });
        expenseBreakdown.push({ label: '🏛️ Property tax', amount: propTax, detail: `1.1% of ${fmt(houseValue)}` });
        setHouseValue(prev => Math.round(prev * (1 + house.appreciation + randBetween(-0.01, 0.02))));
      } else if (newAge >= 22 && !inCollege && (!house || house.cost === 0)) {
        // Single rent: Check if transitioning from shared housing (age 21 → 22)
        const wasInSharedHousing = age < 22 && currentRent !== null && currentRent < 10000; // Shared rent is ~$8,400
        if (currentRent === null || wasInSharedHousing) {
          // First time moving out at 22+ OR transitioning from shared housing — set new base rent ($1,500/month = $18,000/year)
          const baseRent = 18000;
          setCurrentRent(baseRent);
          yearlyExpenses += baseRent;
          expenseBreakdown.push({ label: '🏚️ Rent', amount: baseRent, detail: `${fmt(Math.round(baseRent / 12))}/month${wasInSharedHousing ? ' (moved to own place)' : ''}` });
        } else {
          // Apply inflation to existing rent (lease renewal with inflation adjustment)
          const inflatedRent = Math.round(currentRent * yearInflation);
          setCurrentRent(inflatedRent);
          yearlyExpenses += inflatedRent;
          expenseBreakdown.push({ label: '🏚️ Rent', amount: inflatedRent, detail: `${fmt(Math.round(inflatedRent / 12))}/month (renewed)` });
        }
      } else if (newAge >= 18 && newAge < 22 && !inCollege && (!house || house.cost === 0)) {
        // Shared housing / roommates: Initialize if first time, otherwise apply inflation
        if (currentRent === null) {
          // First time moving out at 18-21 — set base rent for shared housing ($700/month = $8,400/year)
          const baseRent = 8400;
          setCurrentRent(baseRent);
          yearlyExpenses += baseRent;
          expenseBreakdown.push({ label: '🏚️ Rent (shared housing)', amount: baseRent, detail: `${fmt(Math.round(baseRent / 12))}/month` });
        } else {
          // Apply inflation to existing rent
          const inflatedRent = Math.round(currentRent * yearInflation);
          setCurrentRent(inflatedRent);
          yearlyExpenses += inflatedRent;
          expenseBreakdown.push({ label: '🏚️ Rent (shared housing)', amount: inflatedRent, detail: `${fmt(Math.round(inflatedRent / 12))}/month (renewed)` });
        }
      }

      // Insurance / protection costs
      if (!jobInsurance && hasInsurance) {
        const hiCost = Math.round((newAge > 60 ? 6000 : newAge > 40 ? 4000 : 2500) * inflationMultiplier);
        yearlyExpenses += hiCost;
        expenseBreakdown.push({ label: '🏥 Health insurance', amount: hiCost, detail: `${fmt(Math.round(hiCost / 12))}/month` });
      }
      if (!jobDental && hasDental) {
        const dCost = Math.round(600 * inflationMultiplier);
        yearlyExpenses += dCost;
        expenseBreakdown.push({ label: '🦷 Dental insurance', amount: dCost });
      }
      if (hasLifeInsurance) {
        const lifeCost = Math.round(1200 * inflationMultiplier);
        yearlyExpenses += lifeCost;
        expenseBreakdown.push({ label: '💀 Life insurance', amount: lifeCost });
      }
      if (hasDisability && !isRetired) {
        const disCost = Math.round(1800 * inflationMultiplier);
        yearlyExpenses += disCost;
        expenseBreakdown.push({ label: '♿ Disability insurance', amount: disCost });
      }
      if (hasUmbrella) {
        const umbCost = Math.round(400 * inflationMultiplier);
        yearlyExpenses += umbCost;
        expenseBreakdown.push({ label: '☂️ Umbrella policy', amount: umbCost });
      }

      // Student loan payment
      if (studentLoan > 0 && !inCollege) {
        const minPay = Math.min(Math.round(studentLoan * 0.12), studentLoan);
        const interestPortion = Math.round(studentLoan * studentLoanRate);
        const principalPortion = Math.max(0, minPay - interestPortion);
        setStudentLoan(prev => Math.max(0, prev - minPay));
        yearlyExpenses += minPay;
        events.push({ text: `📋 Student loan payment: ${fmt(minPay)} (${fmt(interestPortion)} interest, ${fmt(principalPortion)} principal)`, emoji: '📋' });
        expenseBreakdown.push({ label: '📋 Student loan payment', amount: minPay, detail: `${fmt(interestPortion)} int + ${fmt(principalPortion)} principal` });
      }

      // ==== ITEMIZED LIVING COSTS ====
      const itemizedExpenses = generateItemizedExpenses(newAge, inflationMultiplier, isRetired, kids, house && house.cost > 0, inCollege);
      const livingTotal = itemizedExpenses.reduce((sum, item) => sum + item.amount, 0);
      yearlyExpenses += livingTotal;

      // Push itemized expenses into the breakdown (they already include kid costs)
      expenseBreakdown.push(...itemizedExpenses);

      // Healthcare 65+ (Medicare supplement)
      if (newAge >= 65) {
        const medicareSup = Math.round((3000 + (newAge - 65) * 500) * inflationMultiplier);
        yearlyExpenses += medicareSup;
        expenseBreakdown.push({ label: '🏥 Medicare supplement', amount: medicareSup, detail: `Age ${newAge} — higher health costs` });
      }
    } else {
      // Ages < 18: Kids don't pay for their own expenses — parents cover everything!
      yearlyExpenses = 0;
      expenseBreakdown = [];
    }

    expensesThisYear += yearlyExpenses;
    setCash(prev => prev - yearlyExpenses);

    // ==== INVESTMENT RETURNS ====
    const portf = { ...portfolio };
    let totalGain = 0;
    INVESTMENT_TYPES.forEach(inv => {
      if (portf[inv.key] > 0) {
        let marketReturn = 0;
        switch (diceEvent.type) {
          case 'boom': marketReturn = inv.avgReturn + randBetween(0.05, 0.15); break;
          case 'normal': marketReturn = inv.avgReturn + randBetween(-0.02, 0.05); break;
          case 'downturn': marketReturn = -Math.abs(inv.volatility * randBetween(0.2, 0.6)); break;
          default: marketReturn = inv.avgReturn + randBetween(-0.04, 0.06); break;
        }
        const gain = Math.round(portf[inv.key] * marketReturn);
        portf[inv.key] = Math.max(0, portf[inv.key] + gain);
        totalGain += gain;
      }
    });
    if (totalGain !== 0) {
      events.push({ text: `${totalGain >= 0 ? '📈' : '📉'} Investments: ${totalGain >= 0 ? '+' : ''}${fmt(totalGain)}`, emoji: totalGain >= 0 ? '📈' : '📉' });
    }
    setPortfolio(portf);

    // 401k grows
    if (retirement401k > 0) {
      const r401 = randBetween(0.04, 0.10);
      setRetirement401k(prev => prev + Math.round(prev * r401));
    }

    // ==== OWNED ASSETS: Depreciation + Rental Income ====
    if (ownedAssets.length > 0) {
      let assetRentalInc = 0;
      const updatedAssets = ownedAssets.map(asset => {
        let newVal = asset.currentValue;
        if (asset.depreciation > 0) {
          // Depreciating assets (cars, boats, etc.)
          newVal = Math.max(0, Math.round(asset.currentValue * (1 - asset.depreciation)));
        } else if (asset.depreciation < 0) {
          // Appreciating assets (rental property) — negative depreciation = appreciation
          newVal = Math.round(asset.currentValue * (1 + Math.abs(asset.depreciation) + randBetween(-0.01, 0.02)));
        }
        if (asset.income) {
          const inc = Math.round(asset.income * inflationMultiplier);
          assetRentalInc += inc;
        }
        return { ...asset, currentValue: newVal };
      }).filter(a => a.currentValue > 100); // Remove assets worth essentially nothing
      setOwnedAssets(updatedAssets);
      if (assetRentalInc > 0) {
        setCash(prev => prev + assetRentalInc);
        incomeThisYear += assetRentalInc;
        setRentalIncome(assetRentalInc);
        events.push({ text: `🏘️ Rental income: +${fmt(assetRentalInc)}/year`, emoji: '🏘️' });
      }
    }

    // ==== DICE EVENT ====
    const eventDelta = processEconomicEvent(diceEvent, newAge, events);
    incomeThisYear += eventDelta.income;
    expensesThisYear += eventDelta.expenses;

    // ==== MILESTONE DECISIONS ====
    if (checkMilestone(newAge, events, diceEvent, incomeThisYear, expensesThisYear, expenseBreakdown)) return;

    // ==== DEATH CHECK (gentler curve) ====
    if (newAge >= 78) {
      const deathChance = (newAge - 77) * 0.025;
      if (Math.random() < deathChance) {
        handleDeath(newAge, events);
        return;
      }
    }
    if (newAge >= 100) { handleDeath(newAge, events); return; }

    showYearResult(newAge, events, diceEvent, incomeThisYear, expensesThisYear, expenseBreakdown);
  };

  // ---- Economic events (returns { income, expenses } delta for tracking) ----
  const processEconomicEvent = (diceEvent, currentAge, events) => {
    let eventIncome = 0;
    let eventExpenses = 0;

    switch (diceEvent.type) {
      case 'boom':
        events.push({ text: '📈 Bull market! Economy is booming.', emoji: '📈' });
        if (career && !isRetired && currentAge >= 18) {
          const bonus = Math.round(salary * randBetween(0.02, 0.08));
          setCash(prev => prev + bonus);
          eventIncome += bonus;
          events.push({ text: `🎉 Year-end bonus: +${fmt(bonus)}`, emoji: '🎉' });
        }
        break;
      case 'normal':
        events.push({ text: '📊 Steady year — nothing dramatic.', emoji: '📊' });
        break;
      case 'expense': {
        let expense;
        if (currentAge < 14) {
          expense = rand(EXPENSE_EVENTS_KIDS);
        } else {
          const filtered = EXPENSE_EVENTS.filter(e => (!e.needsHome || (house && house.cost > 0)) && (!e.minAge || currentAge >= e.minAge));
          expense = rand(filtered.length > 0 ? filtered : EXPENSE_EVENTS_KIDS);
        }
        let cost = Math.round(randBetween(expense.min, expense.max) * (currentAge >= 18 ? inflationMultiplier : 1));
        let insNote = '';
        if (expense.insuranceHelps && effectiveInsurance) { cost = Math.round(cost * 0.15); insNote = ` (insurance covered 85%!${jobInsurance ? ' — employer plan' : ''})`; }
        else if (expense.insuranceHelps && !effectiveInsurance) { insNote = ' (no insurance — full cost!)'; }
        if (expense.dentalHelps && effectiveDental) { cost = Math.round(cost * 0.20); insNote = ` (dental plan covered 80%!${jobDental ? ' — employer plan' : ''})`; }
        else if (expense.dentalHelps && !effectiveDental) { insNote = ' (no dental — full cost!)'; }
        // Emergency fund absorbs 50% of unexpected costs
        if (hasEmergencyFund && cost > 500) {
          const absorbed = Math.round(cost * 0.5);
          cost -= absorbed;
          insNote += ` (🏦 emergency fund absorbed ${fmt(absorbed)})`;
        }
        setCash(prev => prev - cost);
        eventExpenses += cost;
        events.push({ text: `${expense.emoji} ${expense.text}: -${fmt(cost)}${insNote}`, emoji: expense.emoji });
        break;
      }
      case 'opportunity':
        if (career && !isRetired && currentAge >= 18) {
          const bump = randBetween(0.05, 0.15);
          const amt = Math.round(salary * bump);
          setSalary(prev => prev + amt);
          events.push({ text: `🎯 Got a raise! +${fmt(amt)}/year (${(bump * 100).toFixed(0)}%)`, emoji: '🎯' });
        } else if (currentAge >= 14 && currentAge < 18) {
          const bonus = Math.round(randBetween(100, 350));
          setCash(prev => prev + bonus);
          eventIncome += bonus;
          events.push({ text: `🎯 Extra tips/bonus: +${fmt(bonus)}`, emoji: '🎯' });
        } else if (currentAge < 14) {
          const found = Math.round(randBetween(10, 50));
          setCash(prev => prev + found);
          eventIncome += found;
          events.push({ text: `🎯 Found some money: +${fmt(found)}`, emoji: '🎯' });
        }
        break;
      case 'downturn':
        events.push({ text: '📉 Market downturn — investments dipped.', emoji: '📉' });
        if (career && !isRetired && Math.random() < 0.10) {
          if (hasDisability) {
            // Disability insurance — partial income protection
            const protPay = Math.round(salary * 0.60);
            setCash(prev => prev + protPay);
            eventIncome += protPay;
            setSalary(prev => Math.round(prev * 0.95));
            events.push({ text: `🏭 Laid off briefly! ♿ Disability insurance covered 60% income (${fmt(protPay)}). Found new job at only 5% pay cut.`, emoji: '🏭' });
          } else {
            const sev = Math.round(salary * 0.30);
            setCash(prev => prev + sev);
            eventIncome += sev;
            setSalary(prev => Math.round(prev * 0.90));
            events.push({ text: `🏭 Laid off! Severance: ${fmt(sev)} — found new job at 10% pay cut`, emoji: '🏭' });
          }
        }
        break;
      case 'major': {
        let event;
        if (currentAge < 14) {
          event = rand(MAJOR_EVENTS_KIDS);
          const amt = Math.round(randBetween(15, 120));
          if (event.type === 'parent_gift') {
            const pGift = Math.round(randBetween(200, 600));
            setPortfolio(prev => ({ ...prev, sp500: prev.sp500 + pGift }));
            events.push({ text: `${event.emoji} ${event.text} +${fmt(pGift)} to investments`, emoji: event.emoji });
          } else {
            setCash(prev => prev + amt);
            eventIncome += amt;
            events.push({ text: `${event.emoji} ${event.text} +${fmt(amt)}`, emoji: event.emoji });
          }
        } else {
          const filtered = MAJOR_EVENTS.filter(e => currentAge >= (e.minAge || 0) && (!e.needsHome || (house && house.cost > 0)));
          event = rand(filtered.length > 0 ? filtered : MAJOR_EVENTS_KIDS);
          if (!event.type || ['prize', 'birthday', 'found', 'parent_gift', 'holiday'].includes(event.type)) {
            const a2 = Math.round(randBetween(50, 300));
            setCash(prev => prev + a2);
            eventIncome += a2;
            events.push({ text: `${event.emoji} ${event.text} +${fmt(a2)}`, emoji: event.emoji });
            break;
          }
          switch (event.type) {
            case 'recession':
              if (career && !isRetired) { setSalary(prev => Math.round(prev * 0.92)); events.push({ text: `${event.emoji} ${event.text} — salary cut 8%`, emoji: event.emoji }); }
              break;
            case 'inheritance': { const a = Math.round(randBetween(15000, 80000)); setCash(prev => prev + a); eventIncome += a; events.push({ text: `${event.emoji} ${event.text} +${fmt(a)}`, emoji: event.emoji }); break; }
            case 'windfall': { const a = Math.round(randBetween(5000, 25000)); setCash(prev => prev + a); eventIncome += a; events.push({ text: `${event.emoji} ${event.text} +${fmt(a)}`, emoji: event.emoji }); break; }
            case 'housing_crash':
              if (house && house.cost > 0) { const l = Math.round(houseValue * 0.20); setHouseValue(prev => prev - l); events.push({ text: `${event.emoji} ${event.text} Home: -${fmt(l)} (will recover)`, emoji: event.emoji }); }
              break;
            case 'tech_boom': {
              const tot = totalInvestments(portfolio);
              if (tot > 0) { const g = Math.round(tot * 0.18); setPortfolio(prev => ({ ...prev, sp500: prev.sp500 + g })); events.push({ text: `${event.emoji} ${event.text} +${fmt(g)}`, emoji: event.emoji }); }
              break;
            }
            case 'inflation':
              setInflationMultiplier(prev => prev * 1.04);
              events.push({ text: `${event.emoji} ${event.text} — costs rising`, emoji: event.emoji });
              break;
            case 'scam': {
              let l = Math.round(randBetween(currentAge < 18 ? 50 : 3000, currentAge < 18 ? 200 : 12000));
              let scamNote = '';
              if (hasUmbrella && l > 1000) { l = Math.round(l * 0.5); scamNote = ' (☂️ umbrella policy helped)'; }
              setCash(prev => prev - l); eventExpenses += l; events.push({ text: `${event.emoji} ${event.text} -${fmt(l)}${scamNote}`, emoji: event.emoji }); break;
            }
            case 'business': { const p = Math.round(randBetween(8000, 40000)); setCash(prev => prev + p); eventIncome += p; events.push({ text: `${event.emoji} ${event.text} +${fmt(p)}`, emoji: event.emoji }); break; }
            case 'disaster':
              if (house && house.cost > 0) {
                const d = Math.round(randBetween(8000, 30000));
                let actual = effectiveInsurance ? Math.round(d * 0.25) : d;
                let disNote = effectiveInsurance ? ' (insured)' : '';
                if (hasEmergencyFund && actual > 1000) { const ab = Math.round(actual * 0.5); actual -= ab; disNote += ` (🏦 emergency fund: -${fmt(ab)})`; }
                if (hasUmbrella) { actual = Math.round(actual * 0.7); disNote += ' (☂️ umbrella policy)'; }
                setCash(prev => prev - actual);
                eventExpenses += actual;
                events.push({ text: `${event.emoji} ${event.text} -${fmt(actual)}${disNote}`, emoji: event.emoji });
              }
              break;
            case 'ipo':
              if (career && !isRetired) { const b = Math.round(salary * randBetween(0.5, 1.5)); setCash(prev => prev + b); eventIncome += b; events.push({ text: `${event.emoji} ${event.text} +${fmt(b)}`, emoji: event.emoji }); }
              break;
            case 'refund': {
              const ref = Math.round(randBetween(1500, 5000));
              setCash(prev => prev + ref);
              eventIncome += ref;
              events.push({ text: `${event.emoji} ${event.text} +${fmt(ref)}`, emoji: event.emoji });
              break;
            }
            case 'workBonus':
              if (career && !isRetired) { const wb = Math.round(salary * randBetween(0.05, 0.15)); setCash(prev => prev + wb); eventIncome += wb; events.push({ text: `${event.emoji} ${event.text} +${fmt(wb)}`, emoji: event.emoji }); }
              break;
            default: events.push({ text: `${event.emoji} ${event.text}`, emoji: event.emoji });
          }
        }
        break;
      }
      default: break;
    }

    return { income: eventIncome, expenses: eventExpenses };
  };

  // ---- Milestones ----
  const checkMilestone = (currentAge, events, diceEvent, income, expenses, breakdown = []) => {
    // Age 25: insurance (only if job doesn't provide)
    if (currentAge === 25 && !effectiveInsurance) {
      setYearEvents(events);
      setPendingYear({ events, diceEvent, income, expenses, age: currentAge, breakdown });
      setCurrentDecision({
        title: '🛡️ Health Insurance (Age 25)',
        description: jobInsurance
          ? "Your employer provides health insurance! But you can also buy additional coverage."
          : "Aging off parents' plan. Buy your own health insurance? ~$2,500/yr but covers 85% of medical costs.",
        options: [
          { label: '✅ Buy Insurance', sublabel: '~$2,500/yr — protects against big bills', action: 'buyInsurance' },
          { label: '❌ Risk It', sublabel: 'Save money, but full cost if something happens', action: 'skipInsurance' },
        ]
      });
      setGameState('decision'); return true;
    }
    if (currentAge === 28 && !isMarried) {
      setYearEvents(events);
      setPendingYear({ events, diceEvent, income, expenses, age: currentAge, breakdown });
      setCurrentDecision({
        title: '💍 Get Married? (Age 28)',
        description: `Wedding costs ~$20K but dual income helps. Your salary: ${fmt(salary)}/yr.`,
        options: [
          { label: '💍 Get Married ($20K)', sublabel: 'Spouse adds $25-40K/yr income', action: 'marry' },
          { label: '🚶 Stay Single', sublabel: 'Full financial independence', action: 'staySingle' },
        ]
      });
      setGameState('decision'); return true;
    }
    if (currentAge === 30 && !house) {
      setYearEvents(events);
      setPendingYear({ events, diceEvent, income, expenses, age: currentAge, breakdown });
      setCurrentDecision({
        title: '🏠 Buy a Home? (Age 30)',
        description: `You have ${fmt(cash)} saved. 20% down payment needed.`,
        options: HOUSE_OPTIONS.map(h => ({
          label: `${h.emoji} ${h.name}${h.cost > 0 ? ` (${fmt(h.cost)})` : ''}`,
          sublabel: h.cost > 0 ? `Down: ${fmt(h.cost * 0.2)} | +${(h.appreciation * 100).toFixed(0)}%/yr | Upkeep: ${fmt(h.maintenance)}/yr` : `Rent: ~${fmt(h.rent)}/yr`,
          action: 'buyHouse', data: h
        }))
      });
      setGameState('decision'); return true;
    }
    if (currentAge === 32) {
      setYearEvents(events);
      setPendingYear({ events, diceEvent, income, expenses, age: currentAge, breakdown });
      setCurrentDecision({
        title: '👶 Start a Family? (Age 32)',
        description: `Child costs ~$10K/yr. Salary: ${fmt(salary)}/yr.`,
        options: [
          { label: '👶 Have a Child', sublabel: '+$10K/yr expenses', action: 'haveKid' },
          { label: '👶👶 Have Twins!', sublabel: '+$20K/yr', action: 'haveTwins' },
          { label: '🚫 Not Now', sublabel: 'Focus on finances', action: 'noKids' },
        ]
      });
      setGameState('decision'); return true;
    }
    if (currentAge === 40 && career && !isRetired) {
      const pool = hasCollege ? CAREERS.college : CAREERS.noCollege;
      const newCareers = pool.filter(c => c.title !== career.title).sort(() => Math.random() - 0.5).slice(0, 3);
      setYearEvents(events);
      setPendingYear({ events, diceEvent, income, expenses, age: currentAge, breakdown });
      setCurrentDecision({
        title: '🔄 Mid-Career Check (Age 40)',
        description: `Currently: ${career.emoji} ${career.title} · ${fmt(salary)}/yr\nBenefits: ${career.benefits?.description || 'None'}`,
        options: [
          ...newCareers.map(c => ({
            label: `${c.emoji} ${c.title} — ${fmt(c.salary)}/yr`,
            sublabel: c.benefits?.description || 'No benefits listed',
            action: 'changeCareer', data: c
          })),
          { label: `✋ Stay as ${career.title}`, sublabel: `Current salary: ${fmt(salary)}/yr`, action: 'keepJob' },
        ]
      });
      setGameState('decision'); return true;
    }
    if (currentAge === 50 && career && !isRetired) {
      setYearEvents(events);
      setPendingYear({ events, diceEvent, income, expenses, age: currentAge, breakdown });
      setCurrentDecision({
        title: '🏦 Catch-Up Contributions (Age 50)',
        description: `Extra 401(k) at 50. 401(k): ${fmt(retirement401k)}. Cash: ${fmt(cash)}.`,
        options: [
          { label: '💰 +$7,500/yr to 401(k)', sublabel: 'Max catch-up contributions', action: 'catchUp', data: { amount: 7500 } },
          { label: '💰 +$3,000/yr to 401(k)', sublabel: 'Moderate increase', action: 'catchUp', data: { amount: 3000 } },
          { label: '❌ Keep Current', sublabel: `6% + ${(jobMatch * 100).toFixed(0)}% match continues`, action: 'noCatchUp' },
        ]
      });
      setGameState('decision'); return true;
    }
    if (currentAge === 62 && career && !isRetired) {
      const estSS = Math.round((ssContribYears * salary * 0.012) / 12) * 12;
      setYearEvents(events);
      setPendingYear({ events, diceEvent, income, expenses, age: currentAge, breakdown });
      setCurrentDecision({
        title: '🏖️ Early Retirement? (Age 62)',
        description: `Retire early (reduced SS: ${fmt(estSS)}/yr) or keep working?\n401(k): ${fmt(retirement401k)} | Investments: ${fmt(totalInvestments(portfolio))} | Cash: ${fmt(cash)}`,
        options: [
          { label: '🏖️ Retire Now', sublabel: `SS: ${fmt(estSS)}/yr (reduced 30%)`, action: 'retireEarly' },
          { label: '💼 Keep Working', sublabel: 'More savings, higher SS at 67', action: 'keepWorking' },
        ]
      });
      setGameState('decision'); return true;
    }
    if (currentAge === 67 && career && !isRetired) {
      const estSS = Math.round((ssContribYears * salary * 0.015) / 12) * 12;
      setYearEvents(events);
      setPendingYear({ events, diceEvent, income, expenses, age: currentAge, breakdown });
      setCurrentDecision({
        title: '🏖️ Full Retirement (Age 67)',
        description: `Full SS: ${fmt(estSS)}/yr.\n401(k): ${fmt(retirement401k)} | Net Worth: ${fmt(getNetWorth())}`,
        options: [
          { label: '🏖️ Retire', sublabel: `Full SS: ${fmt(estSS)}/yr`, action: 'retireFull' },
          { label: '💼 Work to 70', sublabel: 'Delayed SS = +24% more', action: 'keepWorking' },
        ]
      });
      setGameState('decision'); return true;
    }
    if (currentAge === 70 && !isRetired) {
      const estSS = Math.round((ssContribYears * salary * 0.0186) / 12) * 12;
      setIsRetired(true); setRetiredAge(70);
      setJobInsurance(false); setJobDental(false);
      events.push({ text: `🏖️ Retired at 70! Max SS: ${fmt(estSS)}/yr`, emoji: '🏖️' });
    }
    return false;
  };

  // ---- Show year results ----
  const showYearResult = (yearAge, events, diceEvent, income, expenses, breakdown = []) => {
    setYearEvents(events);
    setCurrentEvent({ age: yearAge, diceEvent, events, income, expenses, expenseBreakdown: breakdown });
    setGameState('event');
  };

  const goToActions = () => {
    setCurrentEvent(null);
    setInvestAmounts({});
    setWithdrawAmounts({});
    setDebtPayAmount({ student: '', mortgage: '' });
    setActionsTab(studentLoan > 0 || mortgage > 0 ? 'debt' : 'invest');
    setGameState('actions');
  };

  // ---- Career decisions ----
  const showCareerDecision = (currentAge, events, diceEvent, income, expenses, breakdown = []) => {
    const noCollegeOpts = CAREERS.noCollege.sort(() => Math.random() - 0.5).slice(0, 4);
    setPendingYear({ events, diceEvent, income, expenses, age: currentAge, breakdown });
    setCurrentDecision({
      title: '🎓 Age 18 — Choose Your Path!',
      description: 'College = 4 years (~$80K loans) but higher-paying careers + better benefits after graduation.\nWork now = no debt, start earning immediately.',
      options: [
        { label: '🎓 Go to College (4 yrs, ~$80K loans)', sublabel: 'Higher salaries & better benefits after graduation', action: 'goToCollege' },
        { label: '💼 WORK NOW (No Debt)', action: 'header', isHeader: true },
        ...noCollegeOpts.map(c => ({
          label: `${c.emoji} ${c.title} — ${fmt(c.salary)}/yr`,
          sublabel: `📈 Growth: ${(c.growth * 100).toFixed(1)}%/yr · ${c.benefits?.description || ''}`,
          action: 'pickNoCollegeCareer', data: c
        })),
      ]
    });
    setGameState('decision');
  };

  const showPostCollegeCareerPick = (currentAge, events, diceEvent, income, expenses, breakdown = []) => {
    const opts = CAREERS.college.sort(() => Math.random() - 0.5).slice(0, 5);
    setPendingYear({ events, diceEvent, income, expenses, age: currentAge, breakdown });
    setCurrentDecision({
      title: '🎓 Congratulations, Graduate! Pick Your Career',
      description: `You graduated college! 🎉\nStudent loans: ${fmt(studentLoan)}. Now choose your career — compare salary, growth, and benefits!`,
      options: opts.map(c => ({
        label: `${c.emoji} ${c.title} — ${fmt(c.salary)}/yr`,
        sublabel: `📈 Growth: ${(c.growth * 100).toFixed(1)}%/yr · ${c.benefits?.description || ''}`,
        action: 'pickPostCollegeCareer', data: c
      })),
    });
    setGameState('decision');
  };

  // ---- Death ----
  const handleDeath = (deathAge, events) => {
    let nw = getNetWorth();
    // Life insurance payout to estate
    if (hasLifeInsurance) {
      const payout = 250000;
      nw += payout;
      events.push({ text: `💀 Life insurance paid out ${fmt(payout)} to your estate.`, emoji: '💀' });
    }
    let rating, ratingEmoji;
    if (nw >= 2000000) { rating = 'Millionaire Legacy!'; ratingEmoji = '👑'; }
    else if (nw >= 1000000) { rating = 'Wealthy Estate!'; ratingEmoji = '🌟'; }
    else if (nw >= 500000) { rating = 'Comfortable Life!'; ratingEmoji = '😊'; }
    else if (nw >= 200000) { rating = 'Decent Savings'; ratingEmoji = '👍'; }
    else if (nw >= 50000) { rating = 'Got By'; ratingEmoji = '🤔'; }
    else if (nw >= 0) { rating = 'Barely Made It'; ratingEmoji = '😅'; }
    else { rating = 'Died in Debt'; ratingEmoji = '😰'; }

    setCurrentEvent({ age: deathAge, finished: true, rating, ratingEmoji, nw, events });
    setGameState('finished');
    setScoreSubmitted(false);
    setPlayerRank(null);

    // Auto-submit to leaderboard
    submitScore(deathAge, nw, rating, ratingEmoji);
  };

  // ---- Apply career benefits ----
  const applyCareerBenefits = (careerData) => {
    if (careerData.benefits) {
      setJobInsurance(careerData.benefits.healthIns);
      setJobDental(careerData.benefits.dental);
      setJobMatch(careerData.benefits.match401k);
      if (careerData.benefits.signingBonus > 0) {
        setCash(prev => prev + careerData.benefits.signingBonus);
        addLog(`🎁 Signing bonus: +${fmt(careerData.benefits.signingBonus)}`, 'info');
      }
    }
  };

  // ---- Handle all decisions ----
  const handleDecisionChoice = (option) => {
    if (option.isHeader) return;
    switch (option.action) {
      case 'goToCollege':
        setInCollege(true); setCollegeYear(0);
        addLog('🎓 Enrolled in college! Career chosen after graduation.', 'info');
        break;
      case 'pickNoCollegeCareer':
        setCareer(option.data); setSalary(option.data.salary);
        applyCareerBenefits(option.data);
        addLog(`💼 Started work → ${option.data.emoji} ${option.data.title} (${fmt(option.data.salary)}/yr)`, 'info');
        break;
      case 'pickPostCollegeCareer':
        setCareer(option.data); setSalary(option.data.salary);
        applyCareerBenefits(option.data);
        addLog(`🎓 Career → ${option.data.emoji} ${option.data.title} (${fmt(option.data.salary)}/yr)`, 'info');
        break;
      case 'buyInsurance': setHasInsurance(true); addLog('🛡️ Bought insurance', 'info'); break;
      case 'skipInsurance': addLog('❌ Skipped insurance', 'warning'); break;
      case 'marry':
        setIsMarried(true); setCash(prev => prev - 20000);
        addLog('💍 Got married! Spouse adds income', 'info'); break;
      case 'staySingle': addLog('🚶 Staying single', 'info'); break;
      case 'buyHouse':
        if (option.data.cost === 0) { 
          // Keep renting — set new rent based on current market rate (with inflation)
          const newRent = Math.round(option.data.rent * inflationMultiplier);
          setCurrentRent(newRent);
          setHouse(option.data); 
          addLog(`🏚️ Continuing to rent — ${fmt(Math.round(newRent / 12))}/month`, 'info'); 
        }
        else {
          // Buying a house — no more rent
          const down = Math.round(option.data.cost * 0.2);
          if (cash >= down) {
            setHouse(option.data); setHouseValue(option.data.cost); setMortgage(option.data.cost - down);
            setCurrentRent(null); // No rent when owning
            setCash(prev => prev - down);
            addLog(`${option.data.emoji} Bought ${option.data.name}! Down: ${fmt(down)}, Mortgage: ${fmt(option.data.cost - down)}`, 'info');
          } else { toast.error(`Need ${fmt(down)} down — you have ${fmt(cash)}`); return; }
        }
        break;
      case 'haveKid': setKids(prev => prev + 1); addLog(`👶 Had a child! (${kids + 1})`, 'info'); break;
      case 'haveTwins': setKids(prev => prev + 2); addLog(`👶👶 Twins! (${kids + 2})`, 'info'); break;
      case 'noKids': addLog('🚫 No kids', 'info'); break;
      case 'changeCareer':
        setCareer(option.data); setSalary(option.data.salary);
        applyCareerBenefits(option.data);
        addLog(`🔄 Career switch → ${option.data.emoji} ${option.data.title}`, 'info'); break;
      case 'keepJob': addLog(`✋ Staying as ${career?.title}`, 'info'); break;
      case 'catchUp':
        setRetirement401k(prev => prev + option.data.amount); setCash(prev => prev - option.data.amount);
        addLog(`🏦 +${fmt(option.data.amount)}/yr to 401(k)`, 'info'); break;
      case 'noCatchUp': addLog('❌ No extra 401(k)', 'info'); break;
      case 'retireEarly': {
        const ss = Math.round((ssContribYears * salary * 0.012) / 12) * 12;
        setIsRetired(true); setRetiredAge(62);
        setJobInsurance(false); setJobDental(false);
        addLog(`🏖️ Retired at 62! SS: ${fmt(ss)}/yr`, 'info'); break;
      }
      case 'retireFull': {
        const ss = Math.round((ssContribYears * salary * 0.015) / 12) * 12;
        setIsRetired(true); setRetiredAge(67);
        setJobInsurance(false); setJobDental(false);
        addLog(`🏖️ Retired at 67! Full SS: ${fmt(ss)}/yr`, 'info'); break;
      }
      case 'keepWorking': addLog('💼 Continuing to work', 'info'); break;
      default: break;
    }
    setCurrentDecision(null);
    if (pendingYear) {
      showYearResult(pendingYear.age, pendingYear.events, pendingYear.diceEvent, pendingYear.income, pendingYear.expenses, pendingYear.breakdown || []);
      setPendingYear(null);
    } else {
      goToActions();
    }
  };

  // ---- Actions: invest/withdraw/buy/pay debt ----
  const handleInvest = (key, amount) => {
    if (amount <= 0 || amount > cash) return;
    setCash(prev => prev - amount);
    setPortfolio(prev => ({ ...prev, [key]: prev[key] + amount }));
    addLog(`📈 Invested ${fmt(amount)} in ${INVESTMENT_TYPES.find(i => i.key === key)?.name}`, 'info');
    toast.success(`Invested ${fmt(amount)}`);
    setInvestAmounts(prev => ({ ...prev, [key]: '' }));
  };

  const handleWithdraw = (key, amount) => {
    if (amount <= 0 || amount > portfolio[key]) return;
    setCash(prev => prev + amount);
    setPortfolio(prev => ({ ...prev, [key]: prev[key] - amount }));
    addLog(`📉 Sold ${fmt(amount)} from ${INVESTMENT_TYPES.find(i => i.key === key)?.name}`, 'info');
    toast.success(`Sold ${fmt(amount)}`);
    setWithdrawAmounts(prev => ({ ...prev, [key]: '' }));
  };

  const handlePayDebt = (type, amount) => {
    if (type === 'student') {
      const actual = Math.min(amount, studentLoan, cash);
      if (actual <= 0) return;
      setCash(prev => prev - actual);
      setStudentLoan(prev => Math.max(0, prev - actual));
      const interestSaved = Math.round(actual * studentLoanRate * 5); // rough estimate of interest saved
      addLog(`🎓 Paid ${fmt(actual)} on student loan (saves ~${fmt(interestSaved)} in interest)`, 'info');
      toast.success(`Paid ${fmt(actual)} toward student loans!`);
      if (studentLoan - actual <= 0) toast('🎉 Student loans PAID OFF!', { icon: '🎓' });
      setDebtPayAmount(prev => ({ ...prev, student: '' }));
    } else if (type === 'mortgage') {
      const actual = Math.min(amount, mortgage, cash);
      if (actual <= 0) return;
      setCash(prev => prev - actual);
      setMortgage(prev => Math.max(0, prev - actual));
      const interestSaved = Math.round(actual * mortgageRate * 10);
      addLog(`🏠 Paid ${fmt(actual)} on mortgage (saves ~${fmt(interestSaved)} in interest)`, 'info');
      toast.success(`Paid ${fmt(actual)} toward mortgage!`);
      if (mortgage - actual <= 0) toast('🎉 Mortgage PAID OFF! You own your home!', { icon: '🏠' });
      setDebtPayAmount(prev => ({ ...prev, mortgage: '' }));
    }
  };

  const handlePurchase = (item) => {
    const cost = Math.round(item.cost * (age >= 18 ? inflationMultiplier : 1));
    if (cash < cost) { toast.error(`Need ${fmt(cost)} — you have ${fmt(cash)}`); return; }
    setCash(prev => prev - cost);
    setHappiness(prev => Math.min(100, prev + item.happiness));
    if (item.once) setPurchasedOnce(prev => [...prev, item.once]);
    addLog(`🛒 Bought ${item.emoji} ${item.name} for ${fmt(cost)}`, 'info');
    toast.success(`Bought ${item.name}!`);
  };

  // ---- Buy long-term asset ----
  const handleBuyAsset = (item) => {
    const cost = Math.round(item.cost * (age >= 18 ? inflationMultiplier : 1));
    if (item.special === 'downsize') {
      // Downsize: sell home for 90% value, move to smaller home
      const proceeds = Math.round(houseValue * 0.9);
      const mortLeft = mortgage;
      const net = proceeds - mortLeft;
      setCash(prev => prev + net);
      setMortgage(0);
      setHouseValue(Math.round(houseValue * 0.5));
      setHappiness(prev => Math.min(100, prev + item.happiness));
      setPurchasedOnce(prev => [...prev, item.once]);
      addLog(`🏡 Downsized! Sold for ${fmt(proceeds)}, paid off ${fmt(mortLeft)} mortgage. Net: +${fmt(net)}`, 'info');
      toast.success('Downsized your home!');
      return;
    }
    if (cash < cost) { toast.error(`Need ${fmt(cost)} — you have ${fmt(cash)}`); return; }
    setCash(prev => prev - cost);
    setHappiness(prev => Math.min(100, prev + item.happiness));
    if (item.once) setPurchasedOnce(prev => [...prev, item.once]);
    if (item.addsToHome) {
      // Home improvements add to house value
      setHouseValue(prev => prev + Math.round(item.assetValue * (age >= 18 ? inflationMultiplier : 1)));
      addLog(`🏠 ${item.emoji} ${item.name} — home value +${fmt(item.assetValue)}`, 'info');
    } else {
      // Tracked as owned asset
      const newAsset = {
        name: item.name,
        emoji: item.emoji,
        currentValue: Math.round((item.assetValue || cost) * (age >= 18 ? inflationMultiplier : 1)),
        depreciation: item.depreciation || 0,
        yearBought: age,
        category: item.category || 'other',
        income: item.income || 0,
      };
      setOwnedAssets(prev => [...prev, newAsset]);
      if (item.income) setRentalIncome(prev => prev + item.income);
      addLog(`🏷️ Bought ${item.emoji} ${item.name} for ${fmt(cost)} (asset value: ${fmt(newAsset.currentValue)})`, 'info');
    }
    toast.success(`Bought ${item.name}!`);
  };

  // ---- Buy / toggle financial protection ----
  const handleToggleProtection = (protector) => {
    const isOwned = getProtectionStatus(protector.id);
    if (isOwned) {
      // Cancel protection
      switch (protector.id) {
        case 'health': setHasInsurance(false); break;
        case 'dental': setHasDental(false); break;
        case 'life': setHasLifeInsurance(false); break;
        case 'disability': setHasDisability(false); break;
        case 'umbrella': setHasUmbrella(false); break;
        default: break;
      }
      addLog(`❌ Cancelled ${protector.emoji} ${protector.name}`, 'warning');
      toast.success(`Cancelled ${protector.name}`);
    } else {
      // Buy protection
      if (protector.isOneTime) {
        const cost = Math.round(protector.buyCost * inflationMultiplier);
        if (cash < cost) { toast.error(`Need ${fmt(cost)} — you have ${fmt(cash)}`); return; }
        setCash(prev => prev - cost);
        switch (protector.id) {
          case 'emergency': setHasEmergencyFund(true); break;
          default: break;
        }
        addLog(`${protector.emoji} Set up ${protector.name} for ${fmt(cost)}`, 'info');
      } else {
        // Recurring — just activate; cost is deducted yearly in processYear
        switch (protector.id) {
          case 'health': setHasInsurance(true); break;
          case 'dental': setHasDental(true); break;
          case 'life': setHasLifeInsurance(true); break;
          case 'disability': setHasDisability(true); break;
          case 'umbrella': setHasUmbrella(true); break;
          default: break;
        }
        const annualCost = Math.round(protector.annualCost * inflationMultiplier);
        addLog(`${protector.emoji} Bought ${protector.name} (${fmt(annualCost)}/year)`, 'info');
      }
      toast.success(`Got ${protector.name}!`);
    }
  };

  const getProtectionStatus = (id) => {
    switch (id) {
      case 'health': return hasInsurance || jobInsurance;
      case 'dental': return hasDental || jobDental;
      case 'life': return hasLifeInsurance;
      case 'disability': return hasDisability;
      case 'umbrella': return hasUmbrella;
      case 'emergency': return hasEmergencyFund;
      default: return false;
    }
  };

  const isProtectionFromJob = (id) => {
    if (id === 'health') return jobInsurance;
    if (id === 'dental') return jobDental;
    return false;
  };

  // ---- Get available assets for current age ----
  const getAssetItems = () => {
    let items = [];
    if (age >= 14 && age < 18) items = ASSET_ITEMS.teen || [];
    else if (age >= 18 && !isRetired) items = ASSET_ITEMS.adult || [];
    else if (isRetired) items = ASSET_ITEMS.retired || [];
    return items.filter(it => {
      if (it.minAge && age < it.minAge) return false;
      if (it.needsHome && (!house || house.cost === 0)) return false;
      if (it.once && purchasedOnce.includes(it.once)) return false;
      return true;
    });
  };

  const endYear = () => {
    setGameState('playing');
    setYearEvents([]);
  };

  // ---- Simulate years 1-18 ----
  const simulateEarlyYears = () => {
    // Reset to age 0
    setAge(0); setCash(0); setSalary(0); setCareer(null);
    setHasCollege(false); setInCollege(false); setCollegeYear(0);
    setStudentLoan(0); setMortgage(0); setHouse(null); setHouseValue(0); setCurrentRent(null);
    setPortfolio({ sp500: 1000, bonds: 0, tech: 0, realEstate: 0, crypto: 0 });
    setRetirement401k(0); setSsContribYears(0);
    setChoreIncome(0); setPartTimeIncome(0);
    setHasInsurance(false); setHasDental(false);
    setHasLifeInsurance(false); setHasDisability(false); setHasUmbrella(false); setHasEmergencyFund(false);
    setJobInsurance(false); setJobDental(false); setJobMatch(0.03);
    setIsMarried(false); setKids(0);
    setIsRetired(false); setRetiredAge(null); setHappiness(50);
    setPurchasedOnce([]); setOwnedAssets([]); setRentalIncome(0);
    setInflationMultiplier(1.0);
    setEventLog([{ message: '🍼 Born! Parents opened a $1,000 investment account for you!', type: 'info', age: 0, timestamp: Date.now() }]);

    const recapEvents = [];
    let simCash = 0;
    let simPortfolio = { sp500: 1000, bonds: 0, tech: 0, realEstate: 0, crypto: 0 };
    let simInflation = 1.0;
    let totalIncome = 0;
    let totalExpenses = 0;

    // Simulate years 1-18
    for (let year = 1; year <= 18; year++) {
      const dice = Math.floor(Math.random() * 6) + 1;
      const diceEvent = DICE_EVENTS[dice];
      
      // Inflation
      const yearInflation = 1 + randBetween(0.018, 0.035);
      simInflation *= yearInflation;

      // Ages 1-11: Baby/child
      if (year <= 11) {
        if (dice >= 4) {
          const gift = Math.round(randBetween(25, 120));
          simCash += gift;
          totalIncome += gift;
          recapEvents.push({ age: year, text: `🎂 Birthday/holiday money: +${fmt(gift)}`, emoji: '🎂' });
        }
      }

      // Ages 12-13: Chores
      if (year >= 12 && year <= 13) {
        const weeklyChore = Math.round(randBetween(10, 25));
        const yearlyChore = weeklyChore * 52;
        simCash += yearlyChore;
        totalIncome += yearlyChore;
        recapEvents.push({ age: year, text: `🧹 Chore money: ${fmt(weeklyChore)}/week = ${fmt(yearlyChore)}/year`, emoji: '🧹' });
      }

      // Ages 14-17: Part-time
      if (year >= 14 && year <= 17) {
        const hourlyWage = year >= 16 ? randBetween(10, 14) : randBetween(8, 12);
        const hoursPerWeek = year >= 16 ? randBetween(6, 12) : 3;
        const yearlyPartTime = Math.round(hourlyWage * hoursPerWeek * 50);
        simCash += yearlyPartTime;
        totalIncome += yearlyPartTime;
        recapEvents.push({ age: year, text: `💼 Part-time: ${fmt(hourlyWage.toFixed(0))}/hr × ${hoursPerWeek.toFixed(0)} hrs/wk = ${fmt(yearlyPartTime)}/yr`, emoji: '💼' });
        
        const choreAmt = Math.round(randBetween(500, 1000));
        simCash += choreAmt;
        totalIncome += choreAmt;
      }

      // Investment returns
      INVESTMENT_TYPES.forEach(inv => {
        if (simPortfolio[inv.key] > 0 && year >= inv.minAge) {
          let marketReturn = 0;
          switch (diceEvent.type) {
            case 'boom': marketReturn = inv.avgReturn + randBetween(0.05, 0.15); break;
            case 'normal': marketReturn = inv.avgReturn + randBetween(-0.02, 0.05); break;
            case 'downturn': marketReturn = -Math.abs(inv.volatility * randBetween(0.2, 0.6)); break;
            default: marketReturn = inv.avgReturn + randBetween(-0.04, 0.06); break;
          }
          const gain = Math.round(simPortfolio[inv.key] * marketReturn);
          simPortfolio[inv.key] = Math.max(0, simPortfolio[inv.key] + gain);
        }
      });

      // Economic events (simplified)
      if (diceEvent.type === 'expense' && year >= 12) {
        let expense;
        if (year < 14) {
          expense = rand(EXPENSE_EVENTS_KIDS);
        } else {
          expense = rand(EXPENSE_EVENTS.filter(e => !e.needsHome && (!e.minAge || year >= e.minAge)));
        }
        if (expense) {
          const cost = Math.round(randBetween(expense.min, expense.max));
          simCash = Math.max(0, simCash - cost);
          totalExpenses += cost;
          recapEvents.push({ age: year, text: `${expense.emoji} ${expense.text}: -${fmt(cost)}`, emoji: expense.emoji });
        }
      }

      if (diceEvent.type === 'major' && year < 14) {
        const event = rand(MAJOR_EVENTS_KIDS);
        if (event.type === 'parent_gift') {
          const pGift = Math.round(randBetween(200, 600));
          simPortfolio.sp500 += pGift;
          recapEvents.push({ age: year, text: `${event.emoji} ${event.text} +${fmt(pGift)} to investments`, emoji: event.emoji });
        } else {
          const amt = Math.round(randBetween(15, 120));
          simCash += amt;
          totalIncome += amt;
          recapEvents.push({ age: year, text: `${event.emoji} ${event.text} +${fmt(amt)}`, emoji: event.emoji });
        }
      }
    }

    // Update state
    setAge(18);
    setCash(simCash);
    setPortfolio(simPortfolio);
    setInflationMultiplier(simInflation);
    setChoreIncome(Math.round(randBetween(10, 25) * 52)); // Set based on ages 12-13
    setPartTimeIncome(Math.round(randBetween(8, 14) * randBetween(6, 12) * 50)); // Set based on ages 14-17

    // Show recap
    setRecapData({
      events: recapEvents,
      totalIncome,
      totalExpenses,
      finalCash: simCash,
      finalInvestments: Object.values(simPortfolio).reduce((s, v) => s + v, 0),
      netWorth: simCash + Object.values(simPortfolio).reduce((s, v) => s + v, 0),
    });
    setGameState('recap');
  };

  // ---- New game ----
  const startNewGame = (skipTo18 = false) => {
    setAge(0); setCash(0); setSalary(0); setCareer(null);
    setHasCollege(false); setInCollege(false); setCollegeYear(0);
    setStudentLoan(0); setMortgage(0); setHouse(null); setHouseValue(0); setCurrentRent(null);
    setPortfolio({ sp500: 1000, bonds: 0, tech: 0, realEstate: 0, crypto: 0 });
    setRetirement401k(0); setSsContribYears(0);
    setChoreIncome(0); setPartTimeIncome(0);
    setHasInsurance(false); setHasDental(false);
    setHasLifeInsurance(false); setHasDisability(false); setHasUmbrella(false); setHasEmergencyFund(false);
    setJobInsurance(false); setJobDental(false); setJobMatch(0.03);
    setIsMarried(false); setKids(0);
    setIsRetired(false); setRetiredAge(null); setHappiness(50);
    setPurchasedOnce([]); setOwnedAssets([]); setRentalIncome(0);
    setDiceResult(null); setCurrentEvent(null); setCurrentDecision(null);
    setEventLog([]); setTip(rand(FINANCIAL_TIPS));
    setInflationMultiplier(1.0); setYearSummary(null); setYearEvents([]);
    setPendingYear(null);
    setRecapData(null);
    setScoreSubmitted(false); setPlayerRank(null); setShowLeaderboard(false);
    
    if (skipTo18) {
      simulateEarlyYears();
    } else {
      setGameState('playing');
      setEventLog([{ message: '🍼 Born! Parents opened a $1,000 investment account for you!', type: 'info', age: 0, timestamp: Date.now() }]);
    }
  };

  // ---- Save/Load ----
  const getStateForSave = () => ({
    age, cash, salary, career, hasCollege, inCollege, collegeYear, studentLoan,
    mortgage, house, houseValue, currentRent, portfolio, retirement401k, ssContribYears,
    choreIncome, partTimeIncome, hasInsurance, hasDental,
    hasLifeInsurance, hasDisability, hasUmbrella, hasEmergencyFund,
    jobInsurance, jobDental, jobMatch,
    isMarried, kids, isRetired,
    retiredAge, happiness, purchasedOnce, ownedAssets, rentalIncome,
    inflationMultiplier,
    eventLog: eventLog.slice(0, 30),
  });
  const saveGame = async (idx) => {
    const data = getStateForSave();
    const meta = { saveData: data, career: career?.title, turn: age, cash: Math.round(cash), netWorth: getNetWorth() };
    try {
      const res = await fetch(`${API_BASE_URL}/game-of-life/saves/${idx}`, { method: 'PUT', headers: apiHeaders(), body: JSON.stringify(meta) });
      if (res.ok) { toast.success(`Saved Slot ${idx + 1}!`); fetchSaves(); } else throw new Error();
    } catch {
      const slots = [...saveSlots]; slots[idx] = meta; setSaveSlots(slots);
      localStorage.setItem('investnyou-gol-saves-v4', JSON.stringify(slots));
      toast.success(`Saved (offline) Slot ${idx + 1}`);
    }
  };
  const loadGame = (idx) => {
    const slot = saveSlots[idx]; if (!slot) { toast.error('Empty slot!'); return; }
    const s = slot.saveData || slot;
    setAge(s.age || 0); setCash(s.cash || 0); setSalary(s.salary || 0);
    setCareer(s.career || null); setHasCollege(s.hasCollege || false);
    setInCollege(s.inCollege || false); setCollegeYear(s.collegeYear || 0);
    setStudentLoan(s.studentLoan || 0); setMortgage(s.mortgage || 0);
    setHouse(s.house || null); setHouseValue(s.houseValue || 0); setCurrentRent(s.currentRent || null);
    setPortfolio(s.portfolio || emptyPortfolio()); setRetirement401k(s.retirement401k || 0);
    setSsContribYears(s.ssContribYears || 0); setChoreIncome(s.choreIncome || 0);
    setPartTimeIncome(s.partTimeIncome || 0); setHasInsurance(s.hasInsurance || false);
    setHasDental(s.hasDental || false);
    setHasLifeInsurance(s.hasLifeInsurance || false); setHasDisability(s.hasDisability || false);
    setHasUmbrella(s.hasUmbrella || false); setHasEmergencyFund(s.hasEmergencyFund || false);
    setJobInsurance(s.jobInsurance || false); setJobDental(s.jobDental || false);
    setJobMatch(s.jobMatch || 0.03);
    setIsMarried(s.isMarried || false); setKids(s.kids || 0);
    setIsRetired(s.isRetired || false); setRetiredAge(s.retiredAge || null);
    setHappiness(s.happiness || 50); setPurchasedOnce(s.purchasedOnce || []);
    setOwnedAssets(s.ownedAssets || []); setRentalIncome(s.rentalIncome || 0);
    setInflationMultiplier(s.inflationMultiplier || 1.0);
    setEventLog(s.eventLog || []); setGameState('playing');
    setCurrentEvent(null); setCurrentDecision(null); setYearEvents([]); setPendingYear(null);
    toast.success(`Loaded Slot ${idx + 1}!`);
  };
  const deleteSlot = async (idx) => {
    try {
      const res = await fetch(`${API_BASE_URL}/game-of-life/saves/${idx}`, { method: 'DELETE', headers: apiHeaders() });
      if (res.ok) { toast.success(`Slot ${idx + 1} deleted`); fetchSaves(); } else throw new Error();
    } catch {
      const slots = [...saveSlots]; slots[idx] = null; setSaveSlots(slots);
      localStorage.setItem('investnyou-gol-saves-v4', JSON.stringify(slots));
      toast.success(`Slot ${idx + 1} deleted`);
    }
  };
  const getSlotDisplay = (slot) => {
    if (!slot) return null;
    const name = slot.career || slot.saveData?.career?.title || 'Starting out';
    const emoji = slot.saveData?.career?.emoji || '🎯';
    return { name, emoji, age: slot.turn || slot.saveData?.age || 0, netWorth: slot.netWorth || 0 };
  };

  const getShopItems = () => {
    let items = [];
    if (age < 12) return [];
    if (age >= 12 && age < 14) items = PURCHASABLE_ITEMS.kid;
    else if (age >= 14 && age < 18) items = PURCHASABLE_ITEMS.teen;
    else if (age >= 18 && !isRetired) items = PURCHASABLE_ITEMS.adult;
    else items = PURCHASABLE_ITEMS.retired;
    return items.filter(it => {
      if (it.minAge && age < it.minAge) return false;
      if (it.needsHome && (!house || house.cost === 0)) return false;
      if (it.once && purchasedOnce.includes(it.once)) return false;
      if (it.requires && !purchasedOnce.includes(it.requires)) return false;
      return true;
    });
  };

  const hasDebt = studentLoan > 0 || mortgage > 0;

  // ---- Close recap and proceed to age 18 ----
  const closeRecap = () => {
    setRecapData(null);
    // Trigger age 18 decision
    const events = [];
    const diceEvent = DICE_EVENTS[Math.floor(Math.random() * 6) + 1];
    showCareerDecision(18, events, diceEvent, 0, 0);
  };

  // ============================================
  // RENDER — MENU
  // ============================================
  if (gameState === 'menu') {
    return (
      <div className="gol-container">
        <div className="gol-menu">
          <div className="gol-menu-header">
            <span className="gol-menu-emoji">🎯</span>
            <h1 className="gol-menu-title">The Game of Life</h1>
            <p className="gol-menu-subtitle">A Financial Journey · Ages 1–100</p>
          </div>
          <div className="gol-menu-description">
            <p>Live an entire lifetime making real financial decisions!</p>
            <ul>
              <li>🍼 Born with a <strong>$1,000 investment account</strong></li>
              <li>🧹 Age 12 — Earn from chores</li>
              <li>💼 Age 14 — Part-time job</li>
              <li>🎓 Age 18 — College or work (with <strong>job benefits!</strong>)</li>
              <li>🎲 Roll dice each year — economy is unpredictable</li>
              <li>📈 Invest, buy, and <strong>pay off debt</strong> every year</li>
              <li>🛡️ Jobs include health insurance, 401k match, dental, PTO</li>
              <li>🏠 Buy a house, get married, start a family</li>
              <li>🏖️ Retire with Social Security + 401(k)</li>
            </ul>
          </div>
          <div className="gol-menu-buttons">
            <button className="gol-menu-start-btn" onClick={() => startNewGame(false)}>🎲 Start New Life</button>
            <button className="gol-menu-skip-btn" onClick={() => startNewGame(true)}>⚡ Skip to Age 18</button>
            <button className="gol-menu-leaderboard-btn" onClick={openLeaderboard}>🏆 Leaderboard</button>
          </div>
          <div className="gol-menu-saves">
            <h3>📂 {savesLoading ? 'Loading...' : 'Load Saved Game'}</h3>
            {saveSlots.map((slot, i) => {
              const d = getSlotDisplay(slot);
              return (
                <div key={i} className={`gol-menu-slot ${slot ? 'filled' : 'empty'}`}>
                  <div className="gol-menu-slot-info">
                    <span className="gol-menu-slot-label">Slot {i + 1}</span>
                    {d ? <span className="gol-menu-slot-detail">{d.emoji} {d.name} · Age {d.age} · NW: {fmt(d.netWorth)}</span>
                      : <span className="gol-menu-slot-empty">Empty</span>}
                  </div>
                  <div className="gol-menu-slot-actions">
                    {slot && <>
                      <button className="gol-slot-btn load" onClick={() => loadGame(i)}><FaFolderOpen /></button>
                      <button className="gol-slot-btn delete" onClick={() => deleteSlot(i)}><FaTrash /></button>
                    </>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Leaderboard Modal (accessible from menu) */}
        <AnimatePresence>
          {showLeaderboard && (
            <motion.div className="gol-modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => { setShowLeaderboard(false); setSelectedLeaderboardEntry(null); }}>
              <motion.div className="gol-modal leaderboard" initial={{ scale: .8, y: 50 }} animate={{ scale: 1, y: 0 }} exit={{ scale: .8, y: 50 }} onClick={e => e.stopPropagation()}>
                <div className="gol-lb-header">
                  <FaTrophy className="gol-lb-trophy" />
                  <h2 className="gol-modal-title">Leaderboard</h2>
                  <button className="gol-lb-close" onClick={() => { setShowLeaderboard(false); setSelectedLeaderboardEntry(null); }}>✕</button>
                </div>

                {leaderboardLoading ? (
                  <div className="gol-lb-loading">
                    <div className="gol-lb-spinner" />
                    <p>Loading leaderboard...</p>
                  </div>
                ) : leaderboardData ? (
                  <>
                    {leaderboardData.userBest ? (
                      <div className="gol-lb-user-bar">
                        <div className="gol-lb-user-stat">
                          <span className="gol-lb-user-label">Your Score</span>
                          <span className="gol-lb-user-value">{fmt(leaderboardData.userBest.netWorth)}</span>
                        </div>
                        <div className="gol-lb-user-stat">
                          <span className="gol-lb-user-label">Your Rank</span>
                          <span className="gol-lb-user-value">#{leaderboardData.userRank || '—'}</span>
                        </div>
                        <div className="gol-lb-user-stat">
                          <span className="gol-lb-user-label">Status</span>
                          <span className="gol-lb-user-value">Best save</span>
                        </div>
                      </div>
                    ) : (
                      <div className="gol-lb-user-bar gol-lb-no-entry">
                        <span>🎲 Save a game or finish one to appear on the leaderboard.</span>
                      </div>
                    )}
                    <div className="gol-lb-one-chance-notice">
                      Your best save (by net worth) appears on the leaderboard. Click a player to see their assets.
                    </div>
                    <div className="gol-lb-list">
                      {leaderboardData.entries.length === 0 ? (
                        <div className="gol-lb-empty"><p>No scores yet — be the first! 🎲</p></div>
                      ) : (
                        leaderboardData.entries.map((entry, i) => {
                          const rank = entry.rank;
                          const isTop3 = rank <= 3;
                          const rankIcon = rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : `#${rank}`;
                          return (
                            <div
                              key={entry.id || i}
                              className={`gol-lb-entry ${isTop3 ? `top-${rank}` : ''} ${selectedLeaderboardEntry?.id === entry.id ? 'selected' : ''}`}
                              onClick={() => setSelectedLeaderboardEntry(entry)}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedLeaderboardEntry(entry); } }}
                              aria-label={`${entry.username}, age ${entry.deathAge}, net worth ${entry.netWorth}. Click to view assets.`}
                            >
                              <div className="gol-lb-rank">{rankIcon}</div>
                              <div className="gol-lb-entry-info">
                                <div className="gol-lb-name">{entry.username}</div>
                                <div className="gol-lb-meta">
                                  <span>Age {entry.deathAge}</span>
                                  <span className={`gol-lb-nw-value ${entry.netWorth < 0 ? 'negative' : ''}`}>{fmt(entry.netWorth)}</span>
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                    {selectedLeaderboardEntry && (
                      <div className="gol-lb-profile-overlay" onClick={() => setSelectedLeaderboardEntry(null)}>
                        <div className="gol-lb-profile-detail" onClick={e => e.stopPropagation()}>
                          <div className="gol-lb-profile-header">
                            <h3>{selectedLeaderboardEntry.username}</h3>
                            <button type="button" className="gol-lb-close" onClick={() => setSelectedLeaderboardEntry(null)} aria-label="Close">✕</button>
                          </div>
                          <div className="gol-lb-profile-stats">
                            <span>Age {selectedLeaderboardEntry.deathAge}</span>
                            <span className={selectedLeaderboardEntry.netWorth < 0 ? 'negative' : ''}>{fmt(selectedLeaderboardEntry.netWorth)} net worth</span>
                          </div>
                          <div className="gol-lb-profile-assets">
                            <h4>📦 Assets owned</h4>
                            {selectedLeaderboardEntry.assets && Array.isArray(selectedLeaderboardEntry.assets) && selectedLeaderboardEntry.assets.length > 0 ? (
                              <ul className="gol-lb-asset-list">
                                {selectedLeaderboardEntry.assets.map((a, idx) => (
                                  <li key={idx}>
                                    <span className="gol-lb-asset-name">{a.emoji || ''} {a.name}</span>
                                    <span className="gol-lb-asset-value">{fmt(a.currentValue != null ? a.currentValue : 0)}</span>
                                    {a.income > 0 && <span className="gol-lb-asset-income">+{fmt(Math.round(a.income))}/yr</span>}
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p className="gol-lb-no-assets">No assets recorded for this save.</p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="gol-lb-total">
                      {leaderboardData.total} player{leaderboardData.total !== 1 ? 's' : ''} on the leaderboard
                    </div>
                  </>
                ) : (
                  <div className="gol-lb-empty"><p>Could not load leaderboard.</p></div>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // ============================================
  // RENDER — GAME
  // ============================================
  const lifePercent = Math.min(100, age);
  const phase = age < 12 ? 'childhood' : age < 14 ? 'chores' : age < 18 ? 'parttime' : age < 22 && inCollege ? 'college' : age < 30 ? 'young-adult' : age < 50 ? 'mid-career' : age < 65 ? 'pre-retirement' : 'retirement';
  const totalInv = totalInvestments(portfolio);

  return (
    <div className="gol-container">
      {/* Age & Progress */}
      <div className="gol-age-bar">
        <div className="gol-age-label">
          <span className="gol-age-number">Age {age}</span>
          <span className="gol-age-phase">
            {phase === 'childhood' && '👶 Childhood'}
            {phase === 'chores' && '🧹 Chores Era'}
            {phase === 'parttime' && '💼 Part-Time'}
            {phase === 'college' && '🎓 College'}
            {phase === 'young-adult' && '🧑 Young Adult'}
            {phase === 'mid-career' && '💼 Mid-Career'}
            {phase === 'pre-retirement' && '📊 Pre-Retirement'}
            {phase === 'retirement' && '🏖️ Retirement'}
          </span>
        </div>
        <div className="gol-progress-track">
          <div className="gol-progress-fill" style={{ width: `${lifePercent}%` }}>
            <span className="gol-progress-marker">
              {age < 6 ? '👶' : age < 13 ? '👦' : age < 18 ? '🧑' : age < 65 ? '🧑‍💼' : '👴'}
            </span>
          </div>
          <div className="gol-progress-milestones">
            <span style={{ left: '12%' }} className={age >= 12 ? 'passed' : ''}>12</span>
            <span style={{ left: '14%' }} className={age >= 14 ? 'passed' : ''}>14</span>
            <span style={{ left: '18%' }} className={age >= 18 ? 'passed' : ''}>18</span>
            <span style={{ left: '30%' }} className={age >= 30 ? 'passed' : ''}>30</span>
            <span style={{ left: '50%' }} className={age >= 50 ? 'passed' : ''}>50</span>
            <span style={{ left: '65%' }} className={age >= 65 ? 'passed' : ''}>65</span>
            <span style={{ left: '100%' }} className={age >= 100 ? 'passed' : ''}>100</span>
          </div>
        </div>
      </div>

      {/* Finances */}
      <div className="gol-status-bar">
        <div className="gol-status-item cash">
          <span className="gol-status-label">💰 Cash</span>
          <span className={`gol-status-value ${cash < 0 ? 'negative' : ''}`}>{fmt(cash)}</span>
        </div>
        <div className="gol-status-item investments-stat">
          <span className="gol-status-label">📈 Invested</span>
          <span className="gol-status-value">{fmt(totalInv)}</span>
        </div>
        <div className="gol-status-item net-worth">
          <span className="gol-status-label">📊 Net Worth</span>
          <span className={`gol-status-value ${getNetWorth() < 0 ? 'negative' : ''}`}>{fmt(getNetWorth())}</span>
        </div>
        {career && !isRetired && (
          <div className="gol-status-item salary">
            <span className="gol-status-label">💵 Salary</span>
            <span className="gol-status-value">{fmt(salary)}/yr</span>
          </div>
        )}
        {isRetired && (
          <div className="gol-status-item salary">
            <span className="gol-status-label">🏖️ Retired</span>
            <span className="gol-status-value">Age {retiredAge}</span>
          </div>
        )}
      </div>

      {/* Life Tags */}
      <div className="gol-life-stats">
        {career && <span className="gol-life-tag career">{career.emoji} {career.title}</span>}
        {inCollege && <span className="gol-life-tag college">🎓 College Yr {collegeYear}/4</span>}
        {hasCollege && !inCollege && <span className="gol-life-tag college">🎓 Degree</span>}
        {effectiveInsurance && <span className="gol-life-tag insurance">🏥 {jobInsurance ? 'Employer' : 'Self'} Health</span>}
        {effectiveDental && <span className="gol-life-tag insurance">🦷 Dental</span>}
        {hasLifeInsurance && <span className="gol-life-tag insurance">💀 Life Ins</span>}
        {hasDisability && <span className="gol-life-tag insurance">♿ Disability</span>}
        {hasUmbrella && <span className="gol-life-tag insurance">☂️ Umbrella</span>}
        {hasEmergencyFund && <span className="gol-life-tag insurance">🏦 Emergency Fund</span>}
        {career && !isRetired && <span className="gol-life-tag benefits">📋 {(jobMatch * 100).toFixed(0)}% 401k match</span>}
        {ownedAssets.length > 0 && <span className="gol-life-tag assets">🏷️ {ownedAssets.length} asset{ownedAssets.length > 1 ? 's' : ''}: {fmt(totalAssetValue)}</span>}
        {isMarried && <span className="gol-life-tag married">💍 Married</span>}
        {kids > 0 && <span className="gol-life-tag kids">👶 ×{kids}</span>}
        {house && house.cost > 0 && <span className="gol-life-tag house">{house.emoji} {house.name}</span>}
        {retirement401k > 0 && <span className="gol-life-tag retirement">🏦 401k: {fmt(retirement401k)}</span>}
        {studentLoan > 0 && <span className="gol-life-tag loan">🎓 Loan: {fmt(studentLoan)}</span>}
        {mortgage > 0 && <span className="gol-life-tag loan">🏠 Mortgage: {fmt(mortgage)}</span>}
        <span className="gol-life-tag happiness">😊 {happiness}/100</span>
      </div>

      {/* Dice */}
      {gameState === 'playing' && (
        <div className="gol-spinner-section">
          <div className="gol-spinner-result">
            {diceResult ? (
              <motion.span key={diceResult} initial={{ scale: 1.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="gol-spinner-number">
                {diceResult}
              </motion.span>
            ) : <span className="gol-spinner-placeholder">🎲</span>}
          </div>
          <button className="gol-spin-btn" onClick={rollDice} disabled={isRolling}>
            {isRolling ? '🎰 Rolling...' : `🎲 Roll — Year ${age + 1}`}
          </button>
          {diceResult && DICE_EVENTS[diceResult] && (
            <span className="gol-dice-label">{DICE_EVENTS[diceResult].emoji} {DICE_EVENTS[diceResult].label}</span>
          )}
        </div>
      )}

      {tip && gameState === 'playing' && <div className="gol-tip">{tip}</div>}

      {/* Event Modal */}
      <AnimatePresence>
        {gameState === 'event' && currentEvent && (
          <motion.div className="gol-modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <motion.div className="gol-modal event" initial={{ scale: .8, y: 50 }} animate={{ scale: 1, y: 0 }} exit={{ scale: .8, y: 50 }}>
              <h2 className="gol-modal-title">📅 Age {currentEvent.age} — {currentEvent.diceEvent.emoji} {currentEvent.diceEvent.label}</h2>
              <div className="gol-event-list">
                {currentEvent.events.map((e, i) => (
                  <div key={i} className="gol-event-item">{e.emoji} {e.text}</div>
                ))}
              </div>
              <div className="gol-modal-year-stats">
                {currentEvent.income > 0 && <div className="gol-year-stat gain">+{fmt(currentEvent.income)} income</div>}
                {currentEvent.expenses > 0 && (
                  <div className="gol-year-stat loss" style={{ cursor: 'pointer' }} onClick={() => setShowBreakdown(prev => !prev)}>
                    -{fmt(currentEvent.expenses)} expenses {showBreakdown ? '▲' : '▼ View Details'}
                  </div>
                )}
                {(currentEvent.income > 0 || currentEvent.expenses > 0) && (
                  <div className={`gol-year-stat ${currentEvent.income - currentEvent.expenses >= 0 ? 'gain' : 'loss'}`}>
                    Net: {currentEvent.income - currentEvent.expenses >= 0 ? '+' : ''}{fmt(currentEvent.income - currentEvent.expenses)}
                  </div>
                )}
              </div>

              {/* Itemized Expense Breakdown */}
              {showBreakdown && currentEvent.expenseBreakdown && currentEvent.expenseBreakdown.length > 0 && (
                <div className="gol-expense-breakdown">
                  <div className="gol-breakdown-header">
                    <span>📊 Expense Breakdown</span>
                    <span>{fmt(currentEvent.expenseBreakdown.reduce((s, e) => s + e.amount, 0))} total living costs</span>
                  </div>
                  <div className="gol-breakdown-items">
                    {currentEvent.expenseBreakdown.map((item, i) => (
                      <div key={i} className="gol-breakdown-line">
                        <div className="gol-breakdown-line-main">
                          <span className="gol-breakdown-label">{item.label}</span>
                          <span className="gol-breakdown-amount">{fmt(item.amount)}</span>
                        </div>
                        {item.detail && <div className="gol-breakdown-detail">{item.detail}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="gol-modal-actions">
                <button className="gol-modal-btn" onClick={() => { setShowBreakdown(false); goToActions(); }}>💼 Manage Finances →</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Finished Modal */}
      <AnimatePresence>
        {gameState === 'finished' && currentEvent && (
          <motion.div className="gol-modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <motion.div className="gol-modal finish" initial={{ scale: .8, y: 50 }} animate={{ scale: 1, y: 0 }} exit={{ scale: .8, y: 50 }}>
              <h2 className="gol-modal-title">⚰️ Passed Away at Age {currentEvent.age}</h2>
              <div className="gol-finish-rating">{currentEvent.ratingEmoji} {currentEvent.rating}</div>
              <div className="gol-modal-desc">
                {isRetired ? `Retired at ${retiredAge}` : 'Never retired'}.
                {career ? ` Career: ${career.emoji} ${career.title}.` : ''}
                {isMarried ? ' 💍 Married.' : ''}
                {kids > 0 ? ` 👶 ${kids} kid(s).` : ''}
                {house && house.cost > 0 ? ` ${house.emoji} ${house.name}.` : ''}
              </div>
              <div className="gol-modal-breakdown">
                <div className="gol-breakdown-row"><span>💰 Cash</span><span>{fmt(cash)}</span></div>
                {Object.entries(portfolio).map(([k, v]) => v > 0 ? (
                  <div key={k} className="gol-breakdown-row"><span>{INVESTMENT_TYPES.find(i => i.key === k)?.emoji} {INVESTMENT_TYPES.find(i => i.key === k)?.name}</span><span>{fmt(v)}</span></div>
                ) : null)}
                {retirement401k > 0 && <div className="gol-breakdown-row"><span>🏦 401(k)</span><span>{fmt(retirement401k)}</span></div>}
                {houseValue > 0 && <div className="gol-breakdown-row"><span>🏠 Home</span><span>{fmt(houseValue)}</span></div>}
                {ownedAssets.length > 0 && ownedAssets.map((a, i) => (
                  <div key={`asset-${i}`} className="gol-breakdown-row"><span>{a.emoji} {a.name}</span><span>{fmt(a.currentValue)}</span></div>
                ))}
                {studentLoan > 0 && <div className="gol-breakdown-row negative"><span>🎓 Loans</span><span>-{fmt(studentLoan)}</span></div>}
                {mortgage > 0 && <div className="gol-breakdown-row negative"><span>🏠 Mortgage</span><span>-{fmt(mortgage)}</span></div>}
              </div>
              <div className="gol-finish-nw">Final Net Worth: {fmt(currentEvent.nw)}</div>
              {scoreSubmitted && playerRank && (
                <div className="gol-finish-rank">
                  <FaTrophy className="gol-rank-icon" />
                  <span>Leaderboard Rank: #{playerRank}</span>
                </div>
              )}
              {scoreSubmitted && (
                <div className="gol-finish-submitted">✅ Score submitted to leaderboard!</div>
              )}
              {hasAlreadySubmitted && !scoreSubmitted && (
                <div className="gol-finish-already-submitted">
                  Your best save (by net worth) is on the leaderboard.
                </div>
              )}
              <div className="gol-modal-actions">
                <button className="gol-modal-btn" onClick={openLeaderboard}>🏆 Leaderboard</button>
                <button className="gol-modal-btn" onClick={startNewGame}>🔄 Play Again (for fun)</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Decision Modal */}
      <AnimatePresence>
        {gameState === 'decision' && currentDecision && (
          <motion.div className="gol-modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <motion.div className="gol-modal decision" initial={{ scale: .8, y: 50 }} animate={{ scale: 1, y: 0 }} exit={{ scale: .8, y: 50 }}>
              <h2 className="gol-modal-title">{currentDecision.title}</h2>
              <p className="gol-modal-desc">{currentDecision.description}</p>
              <div className="gol-decision-options">
                {currentDecision.options.map((opt, i) =>
                  opt.isHeader ? (
                    <div key={i} className="gol-decision-header"><span className="gol-decision-header-label">{opt.label}</span></div>
                  ) : (
                    <button key={i} className="gol-decision-btn" onClick={() => handleDecisionChoice(opt)}>
                      <span className="gol-decision-label">{opt.label}</span>
                      {opt.sublabel && <span className="gol-decision-sublabel">{opt.sublabel}</span>}
                    </button>
                  )
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* =============== RECAP MODAL (Ages 1-18) =============== */}
      <AnimatePresence>
        {gameState === 'recap' && recapData && (
          <motion.div className="gol-modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <motion.div className="gol-modal event" initial={{ scale: .8, y: 50 }} animate={{ scale: 1, y: 0 }} exit={{ scale: .8, y: 50 }}>
              <h2 className="gol-modal-title">📚 Your Childhood & Teen Years (Ages 1-18)</h2>
              <p className="gol-modal-desc">
                Here's what happened during your first 18 years! Your parents started you with a $1,000 investment account, 
                and you earned money from chores, part-time jobs, and occasional gifts.
              </p>
              <div className="gol-event-list" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {recapData.events.length > 0 ? (
                  recapData.events.map((e, i) => (
                    <div key={i} className="gol-event-item">
                      <span style={{ fontWeight: 600, color: '#818cf8' }}>Age {e.age}:</span> {e.text}
                    </div>
                  ))
                ) : (
                  <div className="gol-event-item">Growing up normally — no major events!</div>
                )}
              </div>
              <div className="gol-modal-year-stats">
                {recapData.totalIncome > 0 && <div className="gol-year-stat gain">+{fmt(recapData.totalIncome)} total income</div>}
                {recapData.totalExpenses > 0 && <div className="gol-year-stat loss">-{fmt(recapData.totalExpenses)} total expenses</div>}
              </div>
              <div className="gol-modal-breakdown">
                <div className="gol-breakdown-row"><span>💰 Cash at Age 18</span><span>{fmt(recapData.finalCash)}</span></div>
                <div className="gol-breakdown-row"><span>📈 Investments at Age 18</span><span>{fmt(recapData.finalInvestments)}</span></div>
                <div className="gol-breakdown-row" style={{ fontWeight: 700, color: '#818cf8', paddingTop: '8px', borderTop: '1px solid rgba(99,102,241,.2)' }}>
                  <span>📊 Net Worth at Age 18</span><span>{fmt(recapData.netWorth)}</span>
                </div>
              </div>
              <div className="gol-modal-actions">
                <button className="gol-modal-btn" onClick={closeRecap}>🎓 Continue to Age 18 Decision →</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* =============== ACTIONS PANEL =============== */}
      <AnimatePresence>
        {gameState === 'actions' && (
          <motion.div className="gol-actions-panel" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 30 }}>
            <div className="gol-actions-header">
              <h3>💼 Year {age} — Manage Money</h3>
              <span className="gol-actions-cash">💰 {fmt(cash)}</span>
            </div>

            <div className="gol-actions-tabs">
              {hasDebt && <button className={`gol-atab ${actionsTab === 'debt' ? 'active' : ''}`} onClick={() => setActionsTab('debt')}>💳 Debt</button>}
              <button className={`gol-atab ${actionsTab === 'invest' ? 'active' : ''}`} onClick={() => setActionsTab('invest')}>📈 Invest</button>
              {age >= 18 && <button className={`gol-atab ${actionsTab === 'protect' ? 'active' : ''}`} onClick={() => setActionsTab('protect')}>🛡️ Protect</button>}
              <button className={`gol-atab ${actionsTab === 'assets' ? 'active' : ''}`} onClick={() => setActionsTab('assets')}>🏠 Assets</button>
              <button className={`gol-atab ${actionsTab === 'shop' ? 'active' : ''}`} onClick={() => setActionsTab('shop')}>🛒 Buy</button>
            </div>

            {/* ---- DEBT TAB ---- */}
            {actionsTab === 'debt' && (
              <div className="gol-debt-section">
                {studentLoan > 0 && (
                  <div className="gol-debt-card student">
                    <div className="gol-debt-header">
                      <span className="gol-debt-icon">🎓</span>
                      <div className="gol-debt-info">
                        <span className="gol-debt-title">Student Loans</span>
                        <span className="gol-debt-rate">{(studentLoanRate * 100).toFixed(0)}% interest rate</span>
                      </div>
                      <span className="gol-debt-amount">{fmt(studentLoan)}</span>
                    </div>
                    <div className="gol-debt-bar-track">
                      <div className="gol-debt-bar-fill student" style={{ width: '100%' }}></div>
                    </div>
                    <div className="gol-debt-actions">
                      <div className="gol-debt-quick-btns">
                        <button className="gol-quick-pay" onClick={() => handlePayDebt('student', 1000)} disabled={cash < 1000 || studentLoan <= 0}>Pay $1K</button>
                        <button className="gol-quick-pay" onClick={() => handlePayDebt('student', 5000)} disabled={cash < 5000 || studentLoan <= 0}>Pay $5K</button>
                        <button className="gol-quick-pay" onClick={() => handlePayDebt('student', 10000)} disabled={cash < 10000 || studentLoan <= 0}>Pay $10K</button>
                        <button className="gol-quick-pay payoff" onClick={() => handlePayDebt('student', studentLoan)} disabled={cash < studentLoan || studentLoan <= 0}>Pay Off All</button>
                      </div>
                      <div className="gol-invest-input-row">
                        <input type="number" className="gol-invest-input" placeholder="Custom amount"
                          value={debtPayAmount.student} onChange={e => setDebtPayAmount(prev => ({ ...prev, student: e.target.value }))}
                          min="0" max={Math.min(Math.floor(cash), Math.floor(studentLoan))} />
                        <button className="gol-invest-btn sell" onClick={() => handlePayDebt('student', parseInt(debtPayAmount.student) || 0)}
                          disabled={!debtPayAmount.student || parseInt(debtPayAmount.student) <= 0}>Pay</button>
                      </div>
                      <span className="gol-debt-tip">💡 Extra payments save ~{fmt(Math.round(studentLoan * studentLoanRate * 3))} in future interest</span>
                    </div>
                  </div>
                )}
                {mortgage > 0 && (
                  <div className="gol-debt-card mortgage">
                    <div className="gol-debt-header">
                      <span className="gol-debt-icon">🏠</span>
                      <div className="gol-debt-info">
                        <span className="gol-debt-title">Mortgage</span>
                        <span className="gol-debt-rate">{(mortgageRate * 100).toFixed(0)}% interest · Home value: {fmt(houseValue)}</span>
                      </div>
                      <span className="gol-debt-amount">{fmt(mortgage)}</span>
                    </div>
                    <div className="gol-debt-bar-track">
                      <div className="gol-debt-bar-fill mortgage" style={{ width: `${house ? Math.min(100, (mortgage / (house.cost || 1)) * 100) : 100}%` }}></div>
                    </div>
                    <div className="gol-debt-equity">
                      Equity: {fmt(houseValue - mortgage)} ({houseValue > 0 ? ((1 - mortgage / houseValue) * 100).toFixed(0) : 0}% owned)
                    </div>
                    <div className="gol-debt-actions">
                      <div className="gol-debt-quick-btns">
                        <button className="gol-quick-pay" onClick={() => handlePayDebt('mortgage', 5000)} disabled={cash < 5000 || mortgage <= 0}>Pay $5K</button>
                        <button className="gol-quick-pay" onClick={() => handlePayDebt('mortgage', 10000)} disabled={cash < 10000 || mortgage <= 0}>Pay $10K</button>
                        <button className="gol-quick-pay" onClick={() => handlePayDebt('mortgage', 25000)} disabled={cash < 25000 || mortgage <= 0}>Pay $25K</button>
                        <button className="gol-quick-pay payoff" onClick={() => handlePayDebt('mortgage', mortgage)} disabled={cash < mortgage || mortgage <= 0}>Pay Off All</button>
                      </div>
                      <div className="gol-invest-input-row">
                        <input type="number" className="gol-invest-input" placeholder="Custom amount"
                          value={debtPayAmount.mortgage} onChange={e => setDebtPayAmount(prev => ({ ...prev, mortgage: e.target.value }))}
                          min="0" max={Math.min(Math.floor(cash), Math.floor(mortgage))} />
                        <button className="gol-invest-btn sell" onClick={() => handlePayDebt('mortgage', parseInt(debtPayAmount.mortgage) || 0)}
                          disabled={!debtPayAmount.mortgage || parseInt(debtPayAmount.mortgage) <= 0}>Pay</button>
                      </div>
                      <span className="gol-debt-tip">💡 Extra payments save ~{fmt(Math.round(mortgage * mortgageRate * 5))} in future interest</span>
                    </div>
                  </div>
                )}
                {!studentLoan && !mortgage && (
                  <div className="gol-debt-free">🎉 You're debt-free! Great job!</div>
                )}
              </div>
            )}

            {/* ---- INVEST TAB ---- */}
            {actionsTab === 'invest' && (
              <div className="gol-invest-section">
                {INVESTMENT_TYPES.filter(inv => age >= inv.minAge).map(inv => (
                  <div key={inv.key} className="gol-invest-row">
                    <div className="gol-invest-info">
                      <span className="gol-invest-name">{inv.emoji} {inv.name}</span>
                      <span className="gol-invest-detail">{inv.risk} risk · ~{(inv.avgReturn * 100).toFixed(0)}%/yr</span>
                      <span className="gol-invest-balance">Balance: {fmt(portfolio[inv.key])}</span>
                    </div>
                    <div className="gol-invest-controls">
                      <div className="gol-invest-input-row">
                        <input type="number" className="gol-invest-input" placeholder="Amount"
                          value={investAmounts[inv.key] || ''}
                          onChange={e => setInvestAmounts(prev => ({ ...prev, [inv.key]: e.target.value }))}
                          min="0" max={Math.floor(cash)} />
                        <button className="gol-invest-btn buy" onClick={() => handleInvest(inv.key, parseInt(investAmounts[inv.key]) || 0)}
                          disabled={!investAmounts[inv.key] || parseInt(investAmounts[inv.key]) <= 0 || parseInt(investAmounts[inv.key]) > cash}>
                          <FaPlus /> Buy
                        </button>
                      </div>
                      {portfolio[inv.key] > 0 && (
                        <div className="gol-invest-input-row">
                          <input type="number" className="gol-invest-input" placeholder="Amount"
                            value={withdrawAmounts[inv.key] || ''}
                            onChange={e => setWithdrawAmounts(prev => ({ ...prev, [inv.key]: e.target.value }))}
                            min="0" max={Math.floor(portfolio[inv.key])} />
                          <button className="gol-invest-btn sell" onClick={() => handleWithdraw(inv.key, parseInt(withdrawAmounts[inv.key]) || 0)}
                            disabled={!withdrawAmounts[inv.key] || parseInt(withdrawAmounts[inv.key]) <= 0 || parseInt(withdrawAmounts[inv.key]) > portfolio[inv.key]}>
                            <FaMinus /> Sell
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {totalInv > 0 && (
                  <div className="gol-portfolio-summary">
                    <h4>Portfolio Breakdown</h4>
                    <div className="gol-portfolio-bar">
                      {INVESTMENT_TYPES.map(inv => {
                        const pct = totalInv > 0 ? (portfolio[inv.key] / totalInv) * 100 : 0;
                        if (pct < 1) return null;
                        return <div key={inv.key} className={`gol-port-segment ${inv.key}`} style={{ width: `${pct}%` }} title={`${inv.name}: ${pct.toFixed(1)}%`}>{pct >= 10 ? `${inv.emoji} ${pct.toFixed(0)}%` : ''}</div>;
                      })}
                    </div>
                    <div className="gol-portfolio-legend">
                      {INVESTMENT_TYPES.map(inv => portfolio[inv.key] > 0 ? (
                        <span key={inv.key} className="gol-legend-item">{inv.emoji} {inv.name}: {fmt(portfolio[inv.key])} ({((portfolio[inv.key] / totalInv) * 100).toFixed(1)}%)</span>
                      ) : null)}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ---- PROTECT TAB ---- */}
            {actionsTab === 'protect' && (
              <div className="gol-protect-section">
                <p className="gol-protect-intro">🛡️ Financial protectors reduce risk and save you money when bad things happen.</p>
                {PROTECTOR_ITEMS.filter(p => age >= p.minAge).map((protector) => {
                  const owned = getProtectionStatus(protector.id);
                  const fromJob = isProtectionFromJob(protector.id);
                  const annualCost = protector.isOneTime
                    ? Math.round(protector.buyCost * inflationMultiplier)
                    : Math.round(protector.annualCost * inflationMultiplier);
                  return (
                    <div key={protector.id} className={`gol-protect-card ${owned ? 'active' : ''} ${fromJob ? 'employer' : ''}`}>
                      <div className="gol-protect-header">
                        <span className="gol-protect-emoji">{protector.emoji}</span>
                        <div className="gol-protect-info">
                          <span className="gol-protect-name">{protector.name}</span>
                          <span className="gol-protect-benefit">{protector.benefitText}</span>
                        </div>
                        <div className="gol-protect-cost-col">
                          {fromJob ? (
                            <span className="gol-protect-employer-badge">✅ Employer</span>
                          ) : protector.isOneTime ? (
                            <span className="gol-protect-price">{fmt(annualCost)} one-time</span>
                          ) : (
                            <span className="gol-protect-price">{fmt(annualCost)}/yr</span>
                          )}
                        </div>
                      </div>
                      <p className="gol-protect-desc">{protector.description}</p>
                      {!fromJob && (
                        <button
                          className={`gol-protect-btn ${owned ? (protector.isOneTime ? 'owned' : 'cancel') : 'buy'}`}
                          onClick={() => handleToggleProtection(protector)}
                          disabled={(owned && protector.isOneTime) || (!owned && protector.isOneTime && cash < annualCost)}
                        >
                          {owned ? (protector.isOneTime ? '✅ Active' : '❌ Cancel') : (protector.isOneTime ? `Set Up (${fmt(annualCost)})` : `Buy (${fmt(annualCost)}/yr)`)}
                        </button>
                      )}
                      {fromJob && <span className="gol-protect-job-note">Provided by your employer — no cost to you!</span>}
                    </div>
                  );
                })}
                {PROTECTOR_ITEMS.filter(p => age >= p.minAge).length === 0 && (
                  <p className="gol-shop-empty">No protections available at this age.</p>
                )}
              </div>
            )}

            {/* ---- ASSETS TAB ---- */}
            {actionsTab === 'assets' && (
              <div className="gol-assets-section">
                {/* Owned assets list */}
                {ownedAssets.length > 0 && (
                  <div className="gol-owned-assets">
                    <h4 className="gol-assets-subhead">📦 Your Assets</h4>
                    {ownedAssets.map((asset, i) => (
                      <div key={i} className="gol-owned-asset-row">
                        <span className="gol-owned-asset-name">{asset.emoji} {asset.name}</span>
                        <span className="gol-owned-asset-meta">Bought age {asset.yearBought}</span>
                        <span className="gol-owned-asset-value">{fmt(asset.currentValue)}</span>
                        {asset.income > 0 && <span className="gol-owned-asset-income">+{fmt(Math.round(asset.income * inflationMultiplier))}/yr</span>}
                      </div>
                    ))}
                    <div className="gol-owned-asset-total">
                      <span>Total Asset Value</span>
                      <span>{fmt(totalAssetValue)}</span>
                    </div>
                  </div>
                )}
                {/* Buy assets */}
                <h4 className="gol-assets-subhead">🛒 Buy Assets</h4>
                {getAssetItems().length === 0 ? (
                  <p className="gol-shop-empty">No assets available at this age.</p>
                ) : (
                  getAssetItems().map((item, i) => {
                    const cost = Math.round(item.cost * (age >= 18 ? inflationMultiplier : 1));
                    const canBuy = item.special === 'downsize' ? true : cash >= cost;
                    return (
                      <div key={i} className={`gol-shop-item asset ${canBuy ? '' : 'disabled'}`}>
                        <div className="gol-shop-info">
                          <span className="gol-shop-name">{item.emoji} {item.name}</span>
                          <span className="gol-shop-cost">{item.special === 'downsize' ? 'Sell & downsize' : fmt(cost)}</span>
                        </div>
                        <div className="gol-shop-meta">
                          <span className="gol-asset-detail">
                            {item.depreciation > 0 ? `📉 -${(item.depreciation * 100).toFixed(0)}%/yr` : item.depreciation < 0 ? `📈 +${(Math.abs(item.depreciation) * 100).toFixed(0)}%/yr` : ''}
                            {item.income ? ` · 💰 ${fmt(item.income)}/yr` : ''}
                            {item.happiness > 0 ? ` · +${item.happiness} 😊` : ''}
                          </span>
                          <button className="gol-shop-btn" onClick={() => handleBuyAsset(item)} disabled={!canBuy}>
                            {item.special === 'downsize' ? 'Downsize' : canBuy ? 'Buy' : 'Can\'t Afford'}
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {/* ---- SHOP TAB ---- */}
            {actionsTab === 'shop' && (
              <div className="gol-shop-section">
                {getShopItems().length === 0 ? (
                  <p className="gol-shop-empty">Nothing to buy at this age yet!</p>
                ) : (
                  getShopItems().map((item, i) => {
                    const cost = Math.round(item.cost * (age >= 18 ? inflationMultiplier : 1));
                    const canBuy = cash >= cost;
                    return (
                      <div key={i} className={`gol-shop-item ${canBuy ? '' : 'disabled'}`}>
                        <div className="gol-shop-info">
                          <span className="gol-shop-name">{item.emoji} {item.name}</span>
                          <span className="gol-shop-cost">{fmt(cost)}</span>
                        </div>
                        <div className="gol-shop-meta">
                          <span className="gol-shop-happy">+{item.happiness} 😊</span>
                          <button className="gol-shop-btn" onClick={() => handlePurchase(item)} disabled={!canBuy}>
                            {canBuy ? 'Buy' : 'Can\'t Afford'}
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}

            <button className="gol-end-year-btn" onClick={endYear}>✅ End Year {age} →</button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bottom Controls */}
      {(gameState === 'playing' || gameState === 'actions') && (
        <div className="gol-bottom-controls">
          <button className="gol-bottom-btn" onClick={() => setShowLog(!showLog)}>📜 Log</button>
          <button className="gol-bottom-btn" onClick={() => setShowSaveSlots(!showSaveSlots)}>💾 Save</button>
          <button className="gol-bottom-btn" onClick={() => { if (window.confirm('New game? Unsaved progress lost.')) startNewGame(); }}>🔄 New</button>
          <button className="gol-bottom-btn" onClick={() => setGameState('menu')}>📋 Menu</button>
        </div>
      )}

      {showLog && (
        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} className="gol-event-log">
          <h3>📜 Life Log</h3>
          {eventLog.length === 0 ? <p className="gol-log-empty">Roll the dice to begin!</p> : (
            eventLog.map((e, i) => (
              <div key={i} className={`gol-log-entry ${e.type}`}>
                <span className="gol-log-turn">Age {e.age}</span>
                <span className="gol-log-msg">{e.message}</span>
              </div>
            ))
          )}
        </motion.div>
      )}

      {showSaveSlots && (
        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} className="gol-save-panel">
          <h3>💾 Save Slots</h3>
          {saveSlots.map((slot, i) => {
            const d = getSlotDisplay(slot);
            return (
              <div key={i} className={`gol-save-slot ${slot ? 'filled' : 'empty'}`}>
                <div className="gol-slot-info">
                  <span className="gol-slot-number">Slot {i + 1}</span>
                  {d ? <span className="gol-slot-detail">{d.emoji} {d.name} · Age {d.age} · NW: {fmt(d.netWorth)}</span>
                    : <span className="gol-slot-empty-text">Empty</span>}
                </div>
                <div className="gol-slot-actions">
                  <button className="gol-slot-btn save" onClick={() => saveGame(i)} title="Save"><FaSave /></button>
                  {slot && <>
                    <button className="gol-slot-btn load" onClick={() => loadGame(i)} title="Load"><FaFolderOpen /></button>
                    <button className="gol-slot-btn delete" onClick={() => deleteSlot(i)} title="Delete"><FaTrash /></button>
                  </>}
                </div>
              </div>
            );
          })}
        </motion.div>
      )}

      {/* =============== LEADERBOARD MODAL =============== */}
      <AnimatePresence>
        {showLeaderboard && (
          <motion.div className="gol-modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => { setShowLeaderboard(false); setSelectedLeaderboardEntry(null); }}>
            <motion.div className="gol-modal leaderboard" initial={{ scale: .8, y: 50 }} animate={{ scale: 1, y: 0 }} exit={{ scale: .8, y: 50 }} onClick={e => e.stopPropagation()}>
              <div className="gol-lb-header">
                <FaTrophy className="gol-lb-trophy" />
                <h2 className="gol-modal-title">Leaderboard</h2>
                <button className="gol-lb-close" onClick={() => { setShowLeaderboard(false); setSelectedLeaderboardEntry(null); }}>✕</button>
              </div>

              {leaderboardLoading ? (
                <div className="gol-lb-loading">
                  <div className="gol-lb-spinner" />
                  <p>Loading leaderboard...</p>
                </div>
              ) : leaderboardData ? (
                <>
                  {/* User stats bar */}
                  {leaderboardData.userBest ? (
                    <div className="gol-lb-user-bar">
                      <div className="gol-lb-user-stat">
                        <span className="gol-lb-user-label">Your Score</span>
                        <span className="gol-lb-user-value">{fmt(leaderboardData.userBest.netWorth)}</span>
                      </div>
                      <div className="gol-lb-user-stat">
                        <span className="gol-lb-user-label">Your Rank</span>
                        <span className="gol-lb-user-value">#{leaderboardData.userRank || '—'}</span>
                      </div>
                      <div className="gol-lb-user-stat">
                        <span className="gol-lb-user-label">Status</span>
                        <span className="gol-lb-user-value">Best save</span>
                      </div>
                    </div>
                  ) : (
                    <div className="gol-lb-user-bar gol-lb-no-entry">
                      <span>🎲 Save a game or finish one to appear on the leaderboard.</span>
                    </div>
                  )}
                  <div className="gol-lb-one-chance-notice">
                    Your best save (by net worth) appears on the leaderboard. Click a player to see their assets.
                  </div>

                  {/* Entries */}
                  <div className="gol-lb-list">
                    {leaderboardData.entries.length === 0 ? (
                      <div className="gol-lb-empty">
                        <p>No scores yet — be the first! 🎲</p>
                      </div>
                    ) : (
                      leaderboardData.entries.map((entry, i) => {
                        const rank = entry.rank;
                        const isTop3 = rank <= 3;
                        const rankIcon = rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : `#${rank}`;
                        return (
                          <div
                            key={entry.id || i}
                            className={`gol-lb-entry ${isTop3 ? `top-${rank}` : ''} ${selectedLeaderboardEntry?.id === entry.id ? 'selected' : ''}`}
                            onClick={() => setSelectedLeaderboardEntry(entry)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedLeaderboardEntry(entry); } }}
                            aria-label={`${entry.username}, age ${entry.deathAge}, net worth ${entry.netWorth}. Click to view assets.`}
                          >
                            <div className="gol-lb-rank">{rankIcon}</div>
                            <div className="gol-lb-entry-info">
                              <div className="gol-lb-name">{entry.username}</div>
                              <div className="gol-lb-meta">
                                <span>Age {entry.deathAge}</span>
                                <span className={`gol-lb-nw-value ${entry.netWorth < 0 ? 'negative' : ''}`}>{fmt(entry.netWorth)}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                    {selectedLeaderboardEntry && (
                      <div className="gol-lb-profile-overlay" onClick={() => setSelectedLeaderboardEntry(null)}>
                        <div className="gol-lb-profile-detail" onClick={e => e.stopPropagation()}>
                          <div className="gol-lb-profile-header">
                            <h3>{selectedLeaderboardEntry.username}</h3>
                            <button type="button" className="gol-lb-close" onClick={() => setSelectedLeaderboardEntry(null)} aria-label="Close">✕</button>
                          </div>
                          <div className="gol-lb-profile-stats">
                            <span>Age {selectedLeaderboardEntry.deathAge}</span>
                            <span className={selectedLeaderboardEntry.netWorth < 0 ? 'negative' : ''}>{fmt(selectedLeaderboardEntry.netWorth)} net worth</span>
                          </div>
                          <div className="gol-lb-profile-assets">
                            <h4>📦 Assets owned</h4>
                            {selectedLeaderboardEntry.assets && Array.isArray(selectedLeaderboardEntry.assets) && selectedLeaderboardEntry.assets.length > 0 ? (
                              <ul className="gol-lb-asset-list">
                                {selectedLeaderboardEntry.assets.map((a, idx) => (
                                  <li key={idx}>
                                    <span className="gol-lb-asset-name">{a.emoji || ''} {a.name}</span>
                                    <span className="gol-lb-asset-value">{fmt(a.currentValue != null ? a.currentValue : 0)}</span>
                                    {a.income > 0 && <span className="gol-lb-asset-income">+{fmt(Math.round(a.income))}/yr</span>}
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p className="gol-lb-no-assets">No assets recorded for this save.</p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                  <div className="gol-lb-total">
                    {leaderboardData.total} total game{leaderboardData.total !== 1 ? 's' : ''} played
                  </div>
                </>
              ) : (
                <div className="gol-lb-empty">
                  <p>Could not load leaderboard.</p>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Game of Life is a Pro feature — free-tier users see the upgrade card.
// (Everyone has access during pre-alpha.)
const GameOfLifeGate = () => {
  const { state } = useUser();
  const entitlements = getEntitlements(state.user);

  if (!entitlements.gameOfLife) {
    return <ProUpgradeCard feature="The Game of Life simulator" />;
  }

  return <GameOfLife />;
};

export default GameOfLifeGate;
